/**
 * The personalised maker engine (sections/btf-maker.liquid, sections/btf-maker-hero.liquid).
 *
 * One engine for every maker page. A page is a settings module (assets/btf-maker-<page>.js) that
 * lists its designs (layouts), their colours, the fields the customer fills in, and an optional
 * draw() for what HTML cannot draw on its own (the night sky's stars). The engine does the rest,
 * as design_handoff_five_makers/personalised-map/maker.js does in the prototype:
 *
 * - the hero slider: every design on a wall, one after another;
 * - Step 1, the designs as cards, each with an enlarge view, which collapses to a strip once one
 *   is chosen;
 * - Steps 2 and 3, format, size and colour, which collapse together once both are chosen;
 * - Step 4, the fields, and the live preview with its full-screen view, screw caps and share strip;
 * - Step 5, the board on four walls at its true size, and Step 6, the order summary;
 * - Add to basket: the print files are made from the preview (assets/btf-maker-artwork.js) and
 *   posted with the line as Shopify file properties, as Quotes and sayings does.
 *
 * Boards are HTML laid out in cqw, so one drawing serves every size: the cards, the preview, the
 * rooms, the hero and the print file. Sizes come from the product's Size variants; the format is
 * a line-item property. No size is ticked on arrival: the price reads "From £75" and Add to basket
 * asks for a size first.
 *
 * Field types beyond plain text: 'textarea', 'date', 'time', 'place', 'toggle', 'choice' (a row
 * of tabs: options [[value, label, note]]; `hero: true` puts it above the hero heading, `param`
 * lets the address choose it, as ?pet=cat; `hidden: true` keeps it off the page but on the
 * order) and 'photo' (the customer's own photo: upload, zoom, drag to pan on the preview, a
 * print check, and the original file on the basket line). A field's label and value may be
 * functions of the other values (the pet's name follows the pet until the customer types one).
 * A choice with `display: 'cards'` is a grid of picture cards (the pet's styles, drawn by the
 * settings module's styleThumb); 'photozoom' is a photo field's zoom on its own (the photo field
 * says `zoomApart`), so other fields can sit between them.
 *
 * Illustrated styles (the settings module's `ai`, the Pet portrait): sign-in, tries, Illustrate,
 * the versions and the chosen one as the portrait, cut out of its white where asked. The talk with
 * the server and the cut-out are assets/btf-maker-ai.js, loaded the first time it is needed. A
 * design may draw more than one photo (ai.photos and ai.slots: the pet's Best friends), each with
 * its own drawing, versions and framing ('ai', 'ai2'; values aiPic, aiPic2).
 *
 * Choices that shape the set of designs (the Pet portrait's pets, 14 Oct 2026): a layout's
 * `when(values)` leaves it out of the cards, the hero, the strip and shared links; its name and
 * desc may be functions of the values; layoutDefaults may be a function of the values; a field's
 * `when(values, layout)` hides it, and a choice's `allow(values, layout)` narrows its options. A
 * layout's html receives h, the board's height in cqw at that size and format.
 *
 * Showcase colours (the Pet portrait, 14 Oct 2026): a layout's `show` names the colour its card
 * and hero slide show, and the colour it opens in until the customer chooses one in Step 3 (a
 * colour they chose is kept, by name, from design to design). With the settings module's `cycle`
 * (colour names), the cards drift slowly through a few of those colours while Step 1 is open and
 * nothing is chosen yet: only the board's colour custom properties change, cross-faded (the
 * registered --mk-c* properties in assets/btf-maker.css), nothing is laid out or drawn again; it
 * rests while the cards are off screen or the tab is hidden, and never runs with reduced motion.
 *
 * On a computer the preview column keeps the preview in view while Step 4 is filled in (sticky,
 * the board scaled to the window's height: #stickFit).
 */

if (!(window.CSS && CSS.supports && CSS.supports('width: 1cqw'))) {
  import((window.btfAssets && window.btfAssets.cq) || './btf-cq.js').then((m) => m.containerUnits('.btf-mk-board')).catch(() => {});
}

/* ---------------------------------------------------------------- tables */

// Board sizes in cm, width × height in portrait; landscape swaps them
const SIZES = { Small: [40, 55], Medium: [55, 75], Large: [75, 100] };
const SIZE_ORDER = ['Small', 'Medium', 'Large'];
// The pre-cut corner (35 mm), which also centres the caps, and the cap tile's physical size
const CORNER_CM = 3.5;
const CAP_TILE_CM = 6.6;

// The sixteen colours every design can take, after its own (the design's MK_CW16): name, board,
// ink and accent
const CW16 = [
  ['Cream', '#f5efe4', '#2b2420', '#d98a5f'], ['Sage', '#e3e9dd', '#2f4a3a', '#8fab86'], ['Blush', '#f3e1dc', '#5a2e2a', '#e2a598'],
  ['Sky', '#e2ebf2', '#1f3350', '#9bb8d3'], ['White', '#ffffff', '#1d1d1b', '#e53712'], ['Navy', '#1f2d4a', '#f4ead6', '#4a6190'],
  ['Butter', '#f6e7b4', '#3d2f12', '#d9a53a'], ['Peach', '#f6d5bd', '#4a2616', '#e08a5a'], ['Terracotta', '#c8664a', '#fbeee4', '#8e3a24'],
  ['Mustard', '#d9a531', '#2b2010', '#8a5e10'], ['Mint', '#d4ece0', '#1f4535', '#6bb595'], ['Forest', '#2f4a3a', '#efe6d0', '#7d9b78'],
  ['Lavender', '#e3dcef', '#352a52', '#9c8cc4'], ['Stone', '#d9d4cc', '#2b2724', '#8c8379'], ['Charcoal', '#2c2b2a', '#f3efe8', '#8a8580'],
  ['Burgundy', '#5e1f2a', '#f6e6e0', '#b0566a'],
];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const HERO_MS = 3200;

/* --------------------------------------------------------------- helpers */

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const pct = (n) => n.toFixed(3) + '%';
const fold = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const lum = (hex) => {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return 0;
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};
// The caps a colour takes until the customer chooses: white on light boards, black on dark
const capFor = (bg) => (lum(bg) > 0.72 ? 'white' : 'black');
// A chosen place as the field shows it: "Trowbridge, Wiltshire", or "BA14 8BR, Trowbridge"
const placeLabel = (p) => (p.postcode ? p.postcode + ', ' + p.name : p.name + (p.region ? ', ' + p.region : ''));
const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The basket line's copy of the customer's own photo; the studio prints from the artwork
// (Duncan, 19 Oct 2026). Older lines named it "_Original photo (print from this)" or "(backup)".
const PHOTO_PROP = '_Original file';
const oldPhotoProps = (key) => {
  const two = / 2$/.test(key) ? ' 2' : '';
  return [`_Original photo${two} (backup)`, `_Original photo${two} (print from this)`];
};
// The artwork to print and the approval (assets/btf-print-jpeg.js)
const PRINT_FILE = '_Artwork (print from this)';
const APPROVED = 'Approved for print';
const printJpegModule = () => import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js');
// Shopify's file line-item properties take files up to 20 MB
const PHOTO_MAX = 20 * 1048576;

/**
 * A photo the customer chose, the right way up: its full size, and a copy at most 2000 px long
 * for the screen (an object URL). Safari opens HEIC through an <img> where createImageBitmap may
 * not, so that is the second try.
 */
async function decodePhoto(file) {
  let pic = null;
  let url = null;
  try {
    pic = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (e) {
    url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    await img.decode();
    pic = img;
  }
  const w = pic.naturalWidth || pic.width;
  const h = pic.naturalHeight || pic.height;
  if (!w || !h) throw new Error('no picture');
  const k = Math.min(1, 2000 / Math.max(w, h));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * k));
  c.height = Math.max(1, Math.round(h * k));
  const ctx = c.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(pic, 0, 0, c.width, c.height);
  if (pic.close) pic.close();
  if (url) URL.revokeObjectURL(url);
  const small = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.9));
  c.width = c.height = 0;
  if (!small) throw new Error('no picture');
  return { src: URL.createObjectURL(small), w, h };
}

// A photo too big for the basket, as a full-size JPEG under the limit
async function fullCopy(file, max) {
  let pic;
  try {
    pic = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (e) {
    pic = new Image();
    pic.src = URL.createObjectURL(file);
    await pic.decode();
  }
  const w = pic.naturalWidth || pic.width;
  const h = pic.naturalHeight || pic.height;
  // A phone's canvas stops at about 16 million pixels
  const k = Math.min(1, Math.sqrt(16000000 / (w * h)));
  const c = document.createElement('canvas');
  c.width = Math.round(w * k);
  c.height = Math.round(h * k);
  c.getContext('2d').drawImage(pic, 0, 0, c.width, c.height);
  if (pic.close) pic.close();
  try {
    for (const q of [0.92, 0.85, 0.75, 0.6]) {
      const blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', q));
      if (blob && blob.size <= max) return new File([blob], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    }
  } finally {
    c.width = c.height = 0;
  }
  throw new Error('too big');
}

// Minutes the zone is ahead of UTC at a moment
function zoneOffset(ms, tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(ms));
    const g = (t) => Number((parts.find((p) => p.type === t) || {}).value);
    return (Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'), g('second')) - ms) / 60000;
  } catch (e) {
    return 0;
  }
}

/** The helpers a page's designs use: they are the prototype's MakerH */
export const H = {
  esc,
  C: (name, bg, ink, acc, star, extra) => Object.assign({ name, bg, ink, acc: acc || ink, star: star || bg }, extra || {}),
  date: (iso) => {
    const [y, mo, d] = String(iso || '').split('-').map(Number);
    return y ? d + ' ' + MONTHS[mo - 1] + ' ' + y : '';
  },
  dots: (iso) => {
    const [y, mo, d] = String(iso || '').split('-');
    return y ? d + ' · ' + mo + ' · ' + y : '';
  },
  coord: (p) => (p ? Math.abs(p.lat).toFixed(3) + '° ' + (p.lat >= 0 ? 'N' : 'S') + ' · ' + Math.abs(p.lon).toFixed(3) + '° ' + (p.lon >= 0 ? 'E' : 'W') : ''),
  // A local date and time at a place, as a UTC timestamp
  utc: (iso, time, tz) => {
    const [y, mo, d] = String(iso || '2000-01-01').split('-').map(Number);
    const [h, mi] = String(time || '12:00').split(':').map(Number);
    const local = Date.UTC(y || 2000, (mo || 1) - 1, d || 1, h || 0, mi || 0);
    let t = local - zoneOffset(local, tz || 'Europe/London') * 60000;
    t = local - zoneOffset(t, tz || 'Europe/London') * 60000;
    return new Date(t).toISOString().slice(0, 16) + 'Z';
  },
};

function sizeBand(value) {
  const v = String(value || '');
  if (/small/i.test(v)) return 'Small';
  if (/medium/i.test(v)) return 'Medium';
  if (/large/i.test(v)) return 'Large';
  const nums = (v.match(/\d+(\.\d+)?/g) || []).map(Number);
  const big = Math.max(0, ...nums);
  return big >= 90 ? 'Large' : big >= 65 ? 'Medium' : big > 0 ? 'Small' : '';
}

const ICON = {
  max: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" aria-hidden="true"><path d="M8 3H3v5M21 8V3h-5M3 16v5h5M16 21h5v-5"/></svg>',
  prev: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>',
  next: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>',
  x: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
};

/* ---------------------------------------------------------------- engine */

class BtfMaker extends HTMLElement {
  #ready = false;
  #cache = new Map();
  #libs = {};
  #data = {};
  #roomsInView = false;
  #busy = false;
  #hero = { i: 0, timer: 0, paused: false };
  #lb = { open: false, i: 0, mode: 'cards' };
  #drawRun = 0;
  #fitHost = null;
  #cyc = { timer: 0, seen: false, cards: new Map(), stopped: false };

  async connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    try {
      this.setup = JSON.parse(this.querySelector('script[data-mk-setup]').textContent);
    } catch (e) {
      return;
    }
    const [mod, room] = await Promise.all([import(this.dataset.config), import(this.setup.assets.room)]);
    this.roomMod = room;
    this.cfg = mod.default(H, this.setup);
    this.#init();
  }

  /* ----------------------------------------------------------------- state */

  #init() {
    const cfg = this.cfg;
    const S = (this.S = { layout: 0, fmt: cfg.layouts[0].fmt || cfg.fmt || 'port', cw: 0, size: null, caps: null, room: 'desk', tsz: 100, tal: '', v: {}, views: {} });
    const params = new URLSearchParams(window.location.search);
    cfg.fields.forEach((f) => {
      if (typeof f.value === 'function') return;
      S.v[f.key] = f.value && typeof f.value === 'object' ? { ...f.value } : f.value;
      // A choice the address can make: ?pet=cat
      if (f.type === 'choice' && f.param && this.#option(f, params.get(f.param))) S.v[f.key] = params.get(f.param);
    });
    this.touched = { s1: false, s3: false, caps: false };
    // Words the customer has typed or chosen; until then each design shows its own (layoutDefaults)
    this.typed = new Set();
    // The customer's photos, by field: { own, file, w, h, src, name }; a sample until they add one
    this.photos = {};
    // The illustrated portraits: every drawing made on this visit, by what it was drawn from
    // (#aiKey), so going back to a photo, style and background already drawn costs no try
    // history: every drawing this customer has, this visit's and (signed in) earlier ones, newest first
    this.ai = { draws: new Map(), history: [], mineP: null, working: null, session: null, msg: '', lastPan: {} };
    if (cfg.ai) this.#aiAll().forEach((k) => Object.assign(S.v, { ['ai' + this.#sfx(k) + 'X']: 50, ['ai' + this.#sfx(k) + 'Y']: 50, ['ai' + this.#sfx(k) + 'Zoom']: 1 }));
    this.#follow();
    this.#defaults(0);
    // The first design opens in its showcase colour
    S.cw = this.showCw(cfg.layouts[0]);
    this.variants = (this.setup.variants || []).map((v) => ({ ...v, band: sizeBand(v.title) })).filter((v) => v.band);
    this.form = this.querySelector('form[data-mk-form]');
    this.board = this.querySelector('[data-mk-board]');

    // A shared link, or a basket line being edited, opens its design. On the studio print page
    // (data-studio) the line comes from an order instead (#studioHook)
    const edit = window.btfEditLine;
    const studio = this.hasAttribute('data-studio');
    if (studio) {
      /* the line comes from the studio print page */
    } else if (edit && edit.active) {
      Promise.resolve(edit.ready).then(() => {
        const props = (edit.line && edit.line.properties) || {};
        if (props._design) this.#fromParams(new URLSearchParams(props._design), true);
        this.#photosFromLine(props).then(() => this.#aiFromLine(props));
      });
    } else if (params.has('design')) this.#fromParams(params, false);

    this.#drawStatic();
    this.#words();
    this.#listen();
    this.#watchRooms();
    this.#hero_init();
    this.#stickInit();
    this.share = this.querySelector('btf-share');
    if (this.share) {
      this.share.provider = {
        params: () => this.#toParams(),
        // The pictures on the board (their photo, a drawing arriving), so the share pictures follow
        stamp: () => [...this.board.querySelectorAll('.btf-mk-portrait img')].map((i) => i.getAttribute('src')).join('|'),
        tokens: () => ({ design: this.lname(), colour: this.C.name, size: this.S.size || 'Large', price: this.#priceText() }),
        picture: () => this.#picture(),
      };
      this.share.hidden = false;
    }
    window.addEventListener('submit', (e) => this.#submit(e), true);
    if (document.fonts) document.fonts.addEventListener('loadingdone', () => this.#fitAll());
    // The settings module found more pictures to show (the Pet portrait's example drawings)
    document.addEventListener('btf:maker-art', () => {
      this.#fitAll();
      if (this.heroEl) this.#heroDraw();
    });
    this.render();
    if (cfg.ai && cfg.ai.isAi(S.v)) this.#aiSession();
    // Signing in to illustrate leaves the page: the design and photos are kept first, and put
    // back when Shopify returns here (Duncan 16 Oct 2026: they were lost)
    this.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-mk-signin]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      this.#resumeSave().finally(() => (window.location.href = a.href));
    });
    if (!studio && params.get('btf_resume')) this.#resumeLoad();
    else if (this.sharedAi && Object.keys(this.sharedAi).length) this.#aiFromShared();
    if (studio) this.#studioHook();
    else this.#cycleInit();
  }

  get L() {
    return this.cfg.layouts[this.S.layout];
  }

  // Whether a choice allows a design (a layout's `when`: the Pet portrait's sets per pet)
  ok(i, v = this.S.v) {
    const l = this.cfg.layouts[i];
    return !!l && (!l.when || !!l.when(v));
  }

  okList() {
    return this.cfg.layouts.map((l, i) => i).filter((i) => this.ok(i));
  }

  // A design's name and note, which may follow a choice (the planner is for walks, or the vet)
  lname(l = this.L) {
    return typeof l.name === 'function' ? l.name(this.S.v) : l.name;
  }

  ldesc(l = this.L) {
    return (typeof l.desc === 'function' ? l.desc(this.S.v) : l.desc) || '';
  }

  // The format the current design is drawn in: its own, unless it takes either
  get O() {
    const l = this.L;
    return l.o === 'any' ? this.S.fmt : l.o;
  }

  // The design's colours, filled up to sixteen with the house set
  colours(l = this.L) {
    if (!l._cw) {
      const own = l.cw || this.cfg.cw || [];
      const have = new Set(own.map((c) => c.name.toLowerCase().split(' ')[0]));
      const extra = this.cfg.allColours === false ? [] : CW16.filter((c) => !have.has(c[0].toLowerCase())).map((c) => H.C(c[0], c[1], c[2], c[3], c[2]));
      l._cw = own.concat(extra).slice(0, Math.max(16, own.length));
    }
    return l._cw;
  }

  // The colour a design opens in, and shows on its card and hero slide: its showcase colour by
  // name (`show`), or its first
  showCw(l = this.L) {
    if (!l.show) return 0;
    const i = this.colours(l).findIndex((c) => c.name === l.show);
    return i < 0 ? 0 : i;
  }

  get C() {
    const cw = this.colours();
    return cw[this.S.cw] || cw[0];
  }

  get caps() {
    return this.S.caps || capFor(this.C.bg);
  }

  // The board in cm, width × height, for a size (Large until one is chosen) and format
  cm(size = this.S.size || 'Large', o = this.O) {
    const s = SIZES[size] || SIZES.Large;
    return o === 'land' ? [s[1], s[0]] : [s[0], s[1]];
  }

  visible(f, l = this.L) {
    return (!f.for || f.for.includes(l.id)) && (!f.when || !!f.when(this.S.v, l));
  }

  // A choice's options for this design (a layout's own backgrounds): `allow(values, layout)`
  options(f, l = this.L) {
    const allow = f.allow ? f.allow(this.S.v, l) : null;
    return allow ? f.options.filter((o) => allow.includes(o[0])) : f.options;
  }

  // A choice the design does not offer moves to the first one it does
  #coerce() {
    this.cfg.fields.forEach((f) => {
      if (f.type !== 'choice' || !f.allow) return;
      const opts = this.options(f);
      if (opts.length && !opts.some((o) => o[0] === this.S.v[f.key])) this.S.v[f.key] = opts[0][0];
    });
  }

  values(extra) {
    // The chosen drawings, by portrait: aiPic, then aiPic2 for a second pet
    const pics = {};
    if (this.cfg.ai && this.ai) this.#aiAll().forEach((k) => (pics['aiPic' + this.#sfx(k)] = this.#aiPic(k)));
    return Object.assign({}, this.S.v, { tsz: this.S.tsz, tal: this.S.tal }, pics, extra || {});
  }

  // A design's own words (the map's towns), for every field the customer has not filled in
  #own(i) {
    // The words may follow a choice: layoutDefaults(values), the Pet portrait's words per pet
    const all = typeof this.cfg.layoutDefaults === 'function' ? this.cfg.layoutDefaults(this.S.v) : this.cfg.layoutDefaults || {};
    const d = all[this.cfg.layouts[i].id] || {};
    const out = {};
    Object.keys(d).forEach((k) => {
      if (!this.typed.has(k)) out[k] = d[k] && typeof d[k] === 'object' ? { ...d[k] } : d[k];
    });
    return out;
  }

  #defaults(i) {
    Object.assign(this.S.v, this.#own(i));
  }

  // A field's label, which may follow the other values ("Dog's name", "Cat's name")
  label(f) {
    return typeof f.label === 'function' ? f.label(this.S.v, this.L) : f.label;
  }

  #option(f, value) {
    return value != null && (f.options || []).find((o) => o[0] === value);
  }

  // Values that follow other values until the customer makes them their own: a field whose
  // value is a function (the pet's name), and the sample photo of a photo field
  #follow() {
    const S = this.S;
    this.cfg.fields.forEach((f) => {
      if (typeof f.value === 'function' && !this.typed.has(f.key)) S.v[f.key] = f.value(S.v);
      if (f.type !== 'photo') return;
      const ph = this.photos[f.key];
      if (ph && ph.own) return;
      const [src, name, x, y, z] = f.sample ? f.sample(S.v) : ['', '', 50, 50, 1];
      S.v[f.key] = src;
      S.v[f.key + 'X'] = x;
      S.v[f.key + 'Y'] = y;
      S.v[f.key + 'Zoom'] = z;
      this.photos[f.key] = { own: false, name, src };
    });
  }

  /* ------------------------------------------------- back from signing in */

  // The design (as a shared link would carry it) and the customer's own photos, kept in this
  // browser only (IndexedDB) while they sign in, for two hours at most, by page
  #resumeDb() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) return reject(new Error('no IndexedDB'));
      const r = indexedDB.open('btf-maker', 1);
      r.onupgradeneeded = () => r.result.createObjectStore('resume');
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  }

  async #resumeSave() {
    try {
      const photos = Object.entries(this.photos)
        .filter(([, p]) => p && p.own && p.file)
        .map(([key, p]) => ({ key, blob: p.file, name: p.name || 'photo.jpg', type: p.file.type || 'image/jpeg' }));
      const rec = { at: Date.now(), params: this.#toParams().toString(), photos };
      const db = await this.#resumeDb();
      await new Promise((resolve) => {
        const tx = db.transaction('resume', 'readwrite');
        tx.objectStore('resume').put(rec, window.location.pathname);
        tx.oncomplete = tx.onerror = tx.onabort = () => resolve();
      });
    } catch (e) {
      /* signing in still works; the design is simply not kept */
    }
  }

  async #resumeLoad() {
    const url = new URL(window.location.href);
    url.searchParams.delete('btf_resume');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    let rec = null;
    try {
      const db = await this.#resumeDb();
      rec = await new Promise((resolve) => {
        const tx = db.transaction('resume', 'readwrite');
        const store = tx.objectStore('resume');
        const get = store.get(window.location.pathname);
        get.onsuccess = () => {
          resolve(get.result || null);
          store.delete(window.location.pathname);
        };
        get.onerror = () => resolve(null);
      });
    } catch (e) {
      rec = null;
    }
    if (!rec || Date.now() - rec.at > 2 * 3600 * 1000) return;
    this.#fromParams(new URLSearchParams(rec.params), true);
    for (const ph of rec.photos || []) {
      const file = new File([ph.blob], ph.name, { type: ph.type });
      await this.#photoLoad(ph.key, file, this.crops && this.crops[ph.key]);
    }
    this.render();
    // Back where they were: the style and the Illustrate button
    const at = this.querySelector('[data-mk-f="style"]') || this.querySelector('[data-mk-f]');
    if (at) at.scrollIntoView({ block: 'start' });
  }

  /* ---------------------------------------------------------- shared links */

  #toParams() {
    const S = this.S;
    const p = new URLSearchParams();
    p.set('design', this.L.id);
    p.set('orient', this.O === 'land' ? 'landscape' : 'portrait');
    p.set('colour', String(S.cw));
    if (S.size) p.set('size', S.size.toLowerCase());
    if (S.caps) p.set('caps', S.caps);
    if (S.tsz !== 100) p.set('tsz', String(S.tsz));
    if (S.tal) p.set('align', S.tal);
    const views = Object.entries(S.views).filter(([k]) => k.startsWith(this.L.id + ':'));
    if (views.length) p.set('view', views.map(([k, v]) => [k.split(':')[1], v.lat.toFixed(5), v.lon.toFixed(5), v.z.toFixed(2)].join(',')).join(';'));
    // The drawing on the board, so a shared link opens with it (Duncan 16 Oct 2026: it opened on
    // our example). Their photo itself never goes in a link
    if (this.cfg.ai && this.cfg.ai.isAi(S.v)) {
      this.#aiSlots().forEach((slot) => {
        const d = this.#aiDraw0(slot);
        if (d && d.versions[d.ver]) p.set('ai' + this.#sfx(slot), d.versions[d.ver]);
      });
    }
    this.cfg.fields.forEach((f) => {
      if (!this.visible(f) || f.type === 'photozoom') return;
      const v = S.v[f.key];
      if (f.type === 'photo') {
        // A link cannot carry the photo itself, only how the customer's own was framed (for a
        // basket line being edited, which brings the photo back from the line)
        if (this.photos[f.key] && this.photos[f.key].own) p.set(f.key + '_crop', [S.v[f.key + 'X'], S.v[f.key + 'Y'], S.v[f.key + 'Zoom']].map((n) => +Number(n).toFixed(3)).join(','));
      } else if (f.type === 'toggle') p.set(f.key, v ? '1' : '0');
      else if (f.type === 'place' && v) p.set(f.key, [v.name, v.region || '', v.lat, v.lon, v.tz, v.postcode || ''].join('|'));
      // An emptied field that would otherwise follow another (the pet's tagline) is kept empty
      else if (v != null && (v !== '' || typeof f.value === 'function')) p.set(f.key, String(v));
    });
    return p;
  }

  #fromParams(p, edit) {
    const S = this.S;
    const i = this.cfg.layouts.findIndex((l) => l.id === p.get('design'));
    if (i >= 0) S.layout = i;
    if (p.get('orient')) S.fmt = p.get('orient') === 'landscape' ? 'land' : 'port';
    const cw = Number(p.get('colour'));
    if (Number.isInteger(cw) && cw >= 0 && cw < this.colours().length) S.cw = cw;
    const size = (p.get('size') || '').replace(/^./, (c) => c.toUpperCase());
    if (SIZES[size]) S.size = size;
    if (['white', 'black'].includes(p.get('caps'))) S.caps = p.get('caps');
    const tsz = Number(p.get('tsz'));
    if (tsz >= 70 && tsz <= 130) S.tsz = tsz;
    if (['left', 'centre', 'right'].includes(p.get('align'))) S.tal = p.get('align');
    (p.get('view') || '').split(';').forEach((part) => {
      const [i, lat, lon, z] = part.split(',').map(Number);
      if ([i, lat, lon, z].every(Number.isFinite) && z >= 10 && z <= 19) S.views[this.L.id + ':' + i] = { lat, lon, z };
    });
    this.cfg.fields.forEach((f) => {
      if (f.type === 'photo') {
        const [x, y, z] = (p.get(f.key + '_crop') || '').split(',').map(Number);
        this.crops = this.crops || {};
        if (edit && [x, y, z].every(Number.isFinite)) this.crops[f.key] = [Math.min(100, Math.max(0, x)), Math.min(100, Math.max(0, y)), Math.min(f.maxZoom || 2.6, Math.max(1, z))];
        return;
      }
      if (!p.has(f.key)) return;
      const raw = p.get(f.key);
      if (f.type === 'choice') {
        if (this.#option(f, raw)) S.v[f.key] = raw;
      } else if (f.type === 'place') {
        const [name, region, lat, lon, tz, postcode] = raw.split('|');
        if (name && isFinite(lat) && isFinite(lon)) S.v[f.key] = Object.assign({ name, region, lat: Number(lat), lon: Number(lon), tz: tz || 'Europe/London' }, postcode ? { postcode } : {});
      } else if (f.type === 'date') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) S.v[f.key] = raw;
      } else if (f.type === 'time') {
        if (/^\d{2}:\d{2}$/.test(raw)) S.v[f.key] = raw;
      } else if (f.type === 'toggle') {
        S.v[f.key] = raw === '1' || raw === 'true';
      } else if (f.type === 'range') {
        if (isFinite(raw) && raw !== '') S.v[f.key] = Math.min(f.max, Math.max(f.min, Number(raw)));
      } else S.v[f.key] = raw.slice(0, f.max || 60);
    });
    // A shared drawing, put on the board once the page is drawn (#aiFromShared)
    if (!edit && this.cfg.ai) {
      this.sharedAi = {};
      this.#aiAll().forEach((slot) => {
        const u = p.get('ai' + this.#sfx(slot)) || '';
        if (/^https:\/\/btf-ai\.beyondthefridge\.workers\.dev\/img\/g\/[0-9a-f-]{36}\/[1-9]\.(?:png|jpg|webp)$/.test(u)) this.sharedAi[slot] = u;
      });
    }
    this.touched.s1 = this.touched.s3 = true;
    this.cfg.fields.forEach((f) => p.has(f.key) && this.typed.add(f.key));
    // The sample photo follows the pet the link chose
    this.#follow();
    this.cfg.fields.forEach((f) => this.typed.add(f.key));
    if (S.caps) this.touched.caps = true;
    // A link to a design this choice does not offer (a cat's planner on the rabbit's page) opens
    // the first design instead
    if (!this.ok(S.layout)) S.layout = this.okList()[0] || 0;
    this.#coerce();
    if (edit) {
      this.#drawStatic();
      this.#words();
      this.render();
    }
  }

  /* ------------------------------------------------------------- libraries */

  // A vendored script, loaded once, resolving to the global it defines
  lib(key, global) {
    if (!this.#libs[key]) {
      this.#libs[key] = new Promise((resolve, reject) => {
        if (window[global]) return resolve(window[global]);
        const s = document.createElement('script');
        s.src = this.setup.assets[key];
        s.async = true;
        s.onload = () => resolve(window[global]);
        s.onerror = () => reject(new Error('could not load ' + key));
        document.head.appendChild(s);
      });
    }
    return this.#libs[key];
  }

  data(key) {
    if (!this.#data[key]) this.#data[key] = fetch(this.setup.assets[key]).then((r) => r.json());
    return this.#data[key];
  }

  /* ---------------------------------------------------------------- boards */

  /**
   * Draws a design on a board element.
   * @param {HTMLElement} el
   * @param {object} o - { layout, colour, format, values, size, caps, lite }
   */
  paint(el, o) {
    const l = o.layout;
    const c = o.colour;
    const fmt = o.format;
    const cm = this.cm(o.size || 'Large', fmt);
    el.className = 'btf-mk-board is-' + fmt + (o.lite ? ' is-lite' : '');
    el.style.setProperty('--bg', c.bg);
    el.style.setProperty('--ink', c.ink);
    el.style.setProperty('--acc', c.acc);
    el.style.setProperty('--star', c.star);
    el.style.setProperty('--ring', c.ring || 'transparent');
    el.style.setProperty('--mk-ar', cm[0] + ' / ' + cm[1]);
    el.style.setProperty('--mk-rad', ((CORNER_CM * 100) / cm[0]).toFixed(3) + '% / ' + ((CORNER_CM * 100) / cm[1]).toFixed(3) + '%');
    // h: the board's height in cqw at this size and format, for designs drawn to the whole board
    const html = l.html(Object.assign({}, o.values, { o: fmt, c, h: +((cm[1] * 100) / cm[0]).toFixed(3) }), H);
    // A photo already on the board is kept rather than loaded again, so it does not flicker as
    // the words are typed
    const kept = [...el.querySelectorAll('img[data-pan], img[data-keep]')];
    el.innerHTML = '<div class="btf-mk-board__art">' + html + '</div>' + (o.caps ? this.#capsHtml(cm, o.caps) : '');
    if (kept.length) {
      el.querySelectorAll('img[data-pan], img[data-keep]').forEach((n) => {
        const i = kept.findIndex((k) => k.getAttribute('src') === n.getAttribute('src'));
        if (i < 0) return;
        const old = kept.splice(i, 1)[0];
        old.setAttribute('style', n.getAttribute('style') || '');
        n.replaceWith(old);
      });
    }
    this.#typeset(el.firstChild, l, o.values);
    this.#fit(el);
    if (this.cfg.draw) {
      const art = el.firstChild;
      const views = !!o.views;
      const drawing = this.cfg.draw(art, {
        lite: !!o.lite,
        interactive: !!o.interactive,
        layoutId: l.id,
        maker: this,
        lib: (k, g) => this.lib(k, g),
        data: (k) => this.data(k),
        cache: this.#cache,
        // A design's framing (the map's view) is kept per design, for the preview, rooms and print
        view: (key) => (views ? this.S.views[key] : null),
        setView: (key, v) => {
          this.S.views[key] = v;
          this.render();
        },
      }).catch(() => {});
      // The print waits for the preview's skies and maps to be drawn
      if (el === this.board) this.drawing = drawing;
    }
  }

  // Words that would run past the board's edge, or out of their own panel, come down in size
  // until they fit: a long place name, one long word, a line of wide capitals. Every size on a
  // board is in cqw, so the measure is taken on a copy at a fixed width and holds at any size,
  // the cards, the rooms and the print file included (it is measured from the preview).
  #fit(el) {
    const art = el.firstChild;
    if (!art) return;
    if (!this.#fitHost) {
      this.#fitHost = document.createElement('div');
      this.#fitHost.setAttribute('aria-hidden', 'true');
      this.#fitHost.style.cssText = 'position: fixed; left: -10000px; top: 0; width: 600px; visibility: hidden; pointer-events: none;';
    }
    const host = this.#fitHost;
    if (!host.isConnected) document.body.append(host);
    const copy = el.cloneNode(true);
    copy.querySelectorAll('svg').forEach((n) => n.replaceChildren());
    host.replaceChildren(copy);
    const blocks = (a) => [...a.children].filter((b) => !b.matches('svg, img, .btf-mk-credit') && b.textContent.trim());
    const A = copy.firstChild.getBoundingClientRect();
    if (!A.width) return host.replaceChildren();
    const inset = A.width * 0.02;
    const textBox = (b) => {
      const walk = document.createTreeWalker(b, NodeFilter.SHOW_TEXT);
      const r = { l: Infinity, t: Infinity, r: -Infinity, b: -Infinity };
      const rg = document.createRange();
      for (let t = walk.nextNode(); t; t = walk.nextNode()) {
        if (!t.textContent.trim() || t.parentElement.closest('svg')) continue;
        rg.selectNodeContents(t);
        for (const q of rg.getClientRects()) {
          r.l = Math.min(r.l, q.left);
          r.t = Math.min(r.t, q.top);
          r.r = Math.max(r.r, q.right);
          r.b = Math.max(r.b, q.bottom);
        }
      }
      return r;
    };
    // A block whose height is its words' (no height of its own, nor a top and a bottom): the
    // letters of a script face reach past their line, which is no overflow of a block that grows
    // with them. Held to its own bottom, a name alone (no tagline) shrank to almost nothing
    // (Duncan 16 Oct 2026). It is still held inside the board.
    const grows = (b) => {
      const h = b.getBoundingClientRect().height;
      const [hh, bb] = [b.style.height, b.style.bottom];
      b.style.height = 'auto';
      b.style.bottom = 'auto';
      const h2 = b.getBoundingClientRect().height;
      b.style.height = hh;
      b.style.bottom = bb;
      return Math.abs(h - h2) < 1;
    };
    const over = (b, free) => {
      const T = textBox(b);
      if (T.l === Infinity) return false;
      const B = b.getBoundingClientRect();
      return T.l < A.left + inset - 0.5 || T.r > A.right - inset + 0.5 || T.t < A.top - 0.5 || T.b > A.bottom + 0.5 || T.r > B.right + 1 || T.l < B.left - 1 || (!free && T.b > B.bottom + 1);
    };
    const scale = (b, k) =>
      [b, ...b.querySelectorAll('*')].forEach((n) => {
        const fs = n.style && n.style.fontSize;
        if (fs && fs.endsWith('cqw')) n.style.fontSize = (parseFloat(fs) * k).toFixed(3) + 'cqw';
      });
    const mine = blocks(art);
    blocks(copy.firstChild).forEach((b, i) => {
      let k = 1;
      const free = grows(b);
      while (k > 0.15 && over(b, free)) {
        scale(b, 0.92);
        k *= 0.92;
      }
      if (k < 1 && mine[i]) scale(mine[i], k);
    });
    host.replaceChildren();
  }

  #capsHtml(cm, caps) {
    const tw = (CAP_TILE_CM / cm[0]) * 100;
    const th = (CAP_TILE_CM / cm[1]) * 100;
    const ix = (CORNER_CM / cm[0]) * 100;
    const iy = (CORNER_CM / cm[1]) * 100;
    const url = caps === 'black' ? this.setup.assets.capBlack : this.setup.assets.capWhite;
    return [[ix, iy], [100 - ix, iy], [ix, 100 - iy], [100 - ix, 100 - iy]]
      .map(([x, y]) => `<span class="btf-mk-board__cap" style="left: ${pct(x - tw / 2)}; top: ${pct(y - th / 2)}; width: ${pct(tw)}; height: ${pct(th)}; background-image: url('${url}');"></span>`)
      .join('');
  }

  // The customer's text size and alignment, applied to every block of words on the board (any
  // child that holds text, not the skies or maps). Ported from the prototype's typeset().
  #typeset(art, l, v) {
    const k = (v.tsz || 100) / 100;
    const blocks = [...art.children].filter((b) => !b.matches('svg, img, .btf-mk-credit') && !b.querySelector('svg.btf-mk-map, img') && b.textContent.trim());
    const b0 = blocks[0];
    art.dataset.tal0 = b0 && (b0.style.textAlign === 'center' || b0.style.alignItems === 'center') ? 'centre' : b0 && (b0.style.textAlign === 'right' || b0.style.alignItems === 'flex-end') ? 'right' : 'left';
    art.dataset.notype = blocks.length ? '' : '1';
    if (k !== 1) blocks.forEach((b) => [b, ...b.querySelectorAll('*')].forEach((n) => {
      const fs = n.style.fontSize;
      if (/cqw$/.test(fs)) n.style.fontSize = (parseFloat(fs) * k).toFixed(3) + 'cqw';
    }));
    const a = v.tal;
    if (!a || l.noAlign) return;
    blocks.forEach((b) => {
      const s = b.style;
      const hasL = s.left && s.left !== 'auto';
      const hasR = s.right && s.right !== 'auto';
      s.textAlign = a === 'centre' ? 'center' : a;
      if (s.display === 'flex' && s.flexDirection === 'column') s.alignItems = a === 'centre' ? 'center' : a === 'right' ? 'flex-end' : 'flex-start';
      if (hasL !== hasR && !s.width) {
        const edge = hasL ? s.left : s.right;
        const tf = (s.transform || '').replace(/translateX\([^)]*\)/, '').trim();
        if (a === 'centre') { s.left = '50%'; s.right = 'auto'; s.transform = ('translateX(-50%) ' + tf).trim(); }
        else if (a === 'left') { s.left = edge; s.right = 'auto'; s.transform = tf; }
        else { s.right = edge; s.left = 'auto'; s.transform = tf; }
      }
    });
  }

  // The design as the customer has it, for the preview, the rooms and the print
  #current(extra) {
    return Object.assign({ layout: this.L, colour: this.C, format: this.O, values: this.values(), size: this.S.size, caps: this.caps, views: true }, extra || {});
  }

  // A design as a card shows it: its own format and first colour, with the customer's words
  #sample(i) {
    const l = this.cfg.layouts[i];
    const same = i === this.S.layout;
    return {
      layout: l,
      colour: same ? this.C : this.colours(l)[this.showCw(l)],
      format: same ? this.O : l.o === 'any' ? l.fmt || this.cfg.fmt : l.o,
      values: same ? this.values() : this.values(Object.assign({ tsz: 100, tal: '' }, this.#own(i), this.cfg.showValues ? this.cfg.showValues(this.S.v, l) : {})),
      size: 'Large',
      caps: null,
      lite: true,
    };
  }

  /* ------------------------------------------------------- the fixed parts */

  // Everything drawn once, or once per design: the cards, swatches and fields
  #drawStatic() {
    this.#drawCards();
    this.#drawFormats();
    this.#drawSizes();
    this.#drawColours();
    this.#drawFields();
    this.#drawRoomTabs();
  }

  #drawCards() {
    const box = this.querySelector('[data-mk-cards]');
    if (!box) return;
    // Only the designs this choice allows (a pet's own set): the others are left out, and the
    // cards are numbered in the order shown
    const ok = this.okList();
    const n = ok.length;
    box.style.setProperty('--mk-cols', this.cfg.cardCols || (n <= 6 ? n : Math.ceil(n / 2)));
    box.innerHTML = this.cfg.layouts
      .map((l, i) => !ok.includes(i) ? '' : `
        <div class="btf-mk-card${i === this.S.layout ? ' is-on' : ''}" data-mk-card="${i}">
          <button type="button" class="btf-mk-card__pick" data-mk-pick="${i}" aria-pressed="${i === this.S.layout}">
            <span class="btf-mk-card__plate"><span class="btf-mk-board" data-mk-mini="${i}"></span></span>
            <span class="btf-mk-card__name"><b>${String(ok.indexOf(i) + 1).padStart(2, '0')}</b>${esc(this.lname(l))}</span>
            <span class="btf-mk-card__desc">${esc(this.ldesc(l))}</span>
          </button>
          <button type="button" class="btf-mk-zoom" data-mk-enlarge="${i}" aria-label="Enlarge ${esc(this.lname(l))}">${ICON.max}</button>
        </div>`)
      .join('');
  }

  #drawFormats() {
    const box = this.querySelector('[data-mk-formats]');
    if (!box) return;
    const any = this.L.o === 'any';
    box.hidden = !any;
    box.innerHTML = ['land', 'port']
      .map((o) => `<button type="button" class="btf-mk-tab${this.O === o ? ' is-on' : ''}" data-mk-fmt="${o}" aria-pressed="${this.O === o}">${o === 'land' ? 'Landscape' : 'Portrait'}</button>`)
      .join('');
  }

  #drawSizes() {
    const box = this.querySelector('[data-mk-sizes]');
    if (!box) return;
    box.innerHTML = SIZE_ORDER.filter((b) => this.variants.some((v) => v.band === b))
      .map((b) => {
        const cm = this.cm(b);
        const on = this.S.size === b;
        return `<button type="button" class="btf-mk-size${on ? ' is-on' : ''}" data-mk-size="${b}" aria-pressed="${on}"><span class="btf-mk-lbl">${b}</span><span>${cm[0]} × ${cm[1]} cm</span></button>`;
      })
      .join('');
    const help = this.querySelector('[data-mk-sizehelp]');
    if (help) help.textContent = this.S.size ? '' : help.dataset.empty || '';
  }

  #drawColours() {
    const box = this.querySelector('[data-mk-colours]');
    if (!box) return;
    box.innerHTML = this.colours()
      .map((c, i) => `<button type="button" class="btf-mk-swatch${i === this.S.cw ? ' is-on' : ''}" data-mk-cw="${i}" aria-pressed="${i === this.S.cw}"><span class="btf-mk-swatch__chip" style="background: ${c.bg}; color: ${c.ink};">Aa</span><span class="btf-mk-swatch__name">${esc(c.name)}</span></button>`)
      .join('');
  }

  #fieldHtml(f) {
    const v = this.S.v[f.key];
    const id = 'mk-f-' + f.key;
    const text = this.label(f);
    const counted = f.max && (!f.type || f.type === 'text' || f.type === 'textarea');
    const label = `<span class="btf-mk-field__row"><label class="btf-mk-lbl" for="${id}">${esc(text)}${f.optional ? ' <span class="btf-mk-field__opt">(optional)</span>' : ''}</label>${counted ? `<em data-mk-count="${f.key}">${String(v || '').length} / ${f.max}</em>` : ''}</span>`;
    const help = f.help ? `<span class="btf-mk-field__help">${esc(typeof f.help === 'function' ? f.help(this.S.v) : f.help)}</span>` : '';
    // A placeholder may follow the other values ("Bramble’s house rules")
    if (typeof f.placeholder === 'function') f = Object.assign({}, f, { placeholder: f.placeholder(this.S.v) });
    let ctl;
    if (f.type === 'toggle') {
      // On / Off as a pair of tabs, like Landscape / Portrait (the map's street names)
      return `<div class="btf-mk-field${f.half ? '' : ' is-full'}" data-mk-f="${f.key}"><span class="btf-mk-field__row"><span class="btf-mk-lbl" id="${id}">${esc(text)}</span></span><div class="btf-mk-tabs" role="group" aria-labelledby="${id}">${[[true, f.on || 'On'], [false, f.off || 'Off']].map(([val, txt]) => `<button type="button" class="btf-mk-tab${!!v === val ? ' is-on' : ''}" data-mk-toggle="${f.key}" data-mk-val="${val ? 1 : 0}" aria-pressed="${!!v === val}">${esc(txt)}</button>`).join('')}</div>${help}</div>`;
    }
    if (f.type === 'choice' && f.display === 'cards') return this.#cardsHtml(f, id, text, help);
    if (f.type === 'photozoom') return this.#zoomHtml(f);
    // A slider (the travel poster's picture size): min, max, step and a unit for its reading
    if (f.type === 'range') {
      const n = Number(v);
      return `<div class="btf-mk-field${f.half ? '' : ' is-full'}" data-mk-f="${f.key}"><span class="btf-mk-field__row"><label class="btf-mk-lbl" for="${id}">${esc(text)}</label><em data-mk-rangev="${f.key}">${esc(n)}${esc(f.unit || '')}</em></span><input class="btf-mk-range" id="${id}" type="range" min="${f.min}" max="${f.max}" step="${f.step || 1}" value="${esc(n)}" data-mk-range="${f.key}">${help}</div>`;
    }
    if (f.type === 'choice') {
      // One of a few, as tabs, with a note under each name where the options have one
      const opts = this.options(f);
      const notes = opts.some((o) => o[2]);
      return `<div class="btf-mk-field${f.half ? '' : ' is-full'}" data-mk-f="${f.key}"><span class="btf-mk-field__row"><span class="btf-mk-lbl" id="${id}">${esc(text)}</span></span><div class="btf-mk-tabs btf-mk-choice${notes ? ' has-notes' : ''}" role="group" aria-labelledby="${id}">${opts.map((o) => `<button type="button" class="btf-mk-tab${v === o[0] ? ' is-on' : ''}" data-mk-choice="${f.key}" data-mk-val="${esc(o[0])}" aria-pressed="${v === o[0]}"><span>${esc(o[1])}</span>${o[2] ? `<small>${esc(o[2])}</small>` : ''}</button>`).join('')}</div>${help}</div>`;
    }
    if (f.type === 'photo') return this.#photoHtml(f, id, text, help);
    if (f.type === 'place') {
      ctl = `<div class="btf-mk-place" data-mk-place="${f.key}"><input class="btf-mk-input" id="${id}" type="text" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="${id}-list" autocomplete="off" value="${esc(v ? placeLabel(v) : '')}" data-mk-placein="${f.key}"><ul class="btf-mk-place__list" id="${id}-list" role="listbox" hidden></ul></div>`;
    } else if (f.type === 'date' || f.type === 'time') {
      ctl = `<input class="btf-mk-input" id="${id}" type="${f.type}" value="${esc(v)}" data-mk-k="${f.key}"${f.type === 'date' ? ' min="1900-01-01" max="2100-12-31"' : ''} required>`;
    } else if (f.type === 'textarea') {
      ctl = `<textarea class="btf-mk-input btf-mk-input--area" id="${id}" rows="${f.rows || 2}" maxlength="${f.max || 60}" data-mk-k="${f.key}"${f.placeholder ? ` placeholder="${esc(f.placeholder)}"` : ''}${f.optional ? '' : ' required'}>${esc(v)}</textarea>`;
    } else {
      ctl = `<input class="btf-mk-input" id="${id}" type="text" value="${esc(v)}" maxlength="${f.max || 60}" data-mk-k="${f.key}"${f.placeholder ? ` placeholder="${esc(f.placeholder)}"` : ''}${f.optional ? '' : ' required'}>`;
    }
    return `<div class="btf-mk-field${f.half ? '' : ' is-full'}" data-mk-f="${f.key}">${label}${ctl}${help}</div>`;
  }

  /* ---------------------------------------------------------------- photos */

  // The photo field: the photo in use, the upload button, the zoom and recentre, and a note
  // that is the sample's reminder or the print check
  #photoHtml(f, id, text, help) {
    const S = this.S;
    const ph = this.photos[f.key] || {};
    const w = f.words || {};
    const accept = f.accept || 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';
    const up = ph.own ? w.replace || 'Change photo' : w.upload || 'Upload your photo';
    return `<div class="btf-mk-field is-full btf-mk-photo${ph.own ? ' is-own' : ''}" data-mk-f="${f.key}" data-mk-drop="${f.key}">
      <span class="btf-mk-field__row"><span class="btf-mk-lbl" id="${id}">${esc(text)}</span></span>
      <div class="btf-mk-photo__row">
        <span class="btf-mk-photo__thumb">${S.v[f.key] ? `<img src="${esc(S.v[f.key])}" alt="">` : ''}</span>
        <span class="btf-mk-photo__state">${ph.own
          ? `<b>${esc(w.own || 'Your photo is in.')}</b><span>${esc(ph.name || '')}${ph.w ? ` · ${ph.w} × ${ph.h} px` : ''}</span>`
          : `<b>${esc(w.none || 'No photo yet.')}</b><span>${esc(w.sample || 'The preview shows an example until you add yours.')}</span>`}</span>
        <label class="btf-mk-photo__up${ph.own ? '' : ' is-red'}"><input type="file" accept="${accept}" class="visually-hidden" data-mk-photo="${f.key}"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" aria-hidden="true"><path d="M12 16V4M6 10l6-6 6 6M4 20h16"/></svg><span>${esc(up)}</span></label>
      </div>
      <p class="btf-mk-photo__busy" data-mk-pbusy role="status" hidden></p>
      ${help}
      ${f.zoomApart ? '' : this.#zoomInner(f, f.key, id + '-zoom')}
      <div class="btf-mk-photo__note" data-mk-pnote="${f.key}" hidden></div>
    </div>`;
  }

  // A photo's zoom, recentre and drag note. `key` is the picture in the frame: the photo, or the
  // drawn portrait ('ai') once one is chosen, which has its own position and zoom
  #zoomInner(f, key, id) {
    const w = f.words || {};
    const z = this.S.v[key + 'Zoom'];
    return `<div class="btf-mk-photo__zoom">
        <span class="btf-mk-field__row"><label class="btf-mk-lbl" for="${id}">${esc((typeof w.zoom === 'function' ? w.zoom(this.S.v, this.L) : w.zoom) || 'Zoom')}</label><em data-mk-pzv="${key}">${Number(z).toFixed(2)}×</em></span>
        <input class="btf-mk-range" id="${id}" type="range" min="1" max="${f.maxZoom || 2.6}" step="0.05" value="${esc(z)}" data-mk-pz="${key}">
        <span class="btf-mk-photo__drag"><span>${esc(w.drag || 'Drag the photo in the preview to move it in the frame.')}</span><button type="button" class="btf-mk-link" data-mk-recentre="${key}">${esc(w.recentre || 'Recentre')}</button></span>
      </div>`;
  }

  // A photo field's zoom, set apart from it (a 'photozoom' field)
  #zoomHtml(z) {
    const f = this.cfg.fields.find((x) => x.key === z.photo && x.type === 'photo');
    if (!f) return '';
    const key = this.#panKey(f.key);
    this.ai.lastPan[z.key] = key;
    return `<div class="btf-mk-field is-full btf-mk-photo btf-mk-photo--zoom" data-mk-f="${z.key}">${this.#zoomInner(f, key, 'mk-f-' + z.key)}</div>`;
  }

  // The picture a photo field's frame shows: the photo, or the chosen drawing
  #panKey(key) {
    return this.cfg.ai && this.#aiAll().includes(key) && this.#aiPic(key) ? 'ai' + this.#sfx(key) : key;
  }

  // A choice as picture cards (the pet's styles): the settings module draws each card's picture
  #cardsHtml(f, id, text, help) {
    const v = this.S.v[f.key];
    const n = f.options.length;
    // The note under the cards may follow the values (the pet portrait's examples, per pet)
    const sn = this.cfg.ai && this.cfg.ai.words.stylesNote;
    const note = typeof sn === 'function' ? sn(this.S.v, this.L) : sn;
    return `<div class="btf-mk-field is-full" data-mk-f="${f.key}">${this.cfg.styleDefs || ''}
      <span class="btf-mk-field__row"><span class="btf-mk-lbl" id="${id}">${esc(text)}</span></span>
      <div class="btf-mk-styles" role="group" aria-labelledby="${id}" style="--mk-cols: ${n <= 6 ? n : Math.ceil(n / 2)};">${f.options
        .map((o, i) => {
          const s = (this.cfg.styles || []).find((x) => x.id === o[0]) || { id: o[0], name: o[1] };
          const on = v === o[0];
          return `<button type="button" class="btf-mk-style${on ? ' is-on' : ''}" data-mk-choice="${f.key}" data-mk-val="${esc(o[0])}" aria-pressed="${on}"${o[2] ? ` title="${esc(o[2])}"` : ''}><span class="btf-mk-style__plate">${this.cfg.styleThumb ? this.cfg.styleThumb(s, this.S.v) : ''}</span><span class="btf-mk-style__name"><b>${String(i + 1).padStart(2, '0')}</b>${esc(o[1])}</span></button>`;
        })
        .join('')}</div>
      ${note ? `<p class="btf-mk-field__help" data-mk-stylesnote>${esc(note)}</p>` : ''}${help}</div>`;
  }

  // A photo in: decoded the right way up, with a copy at most 2000 px long for the screen. The
  // original file is kept for the order.
  async #photoLoad(key, file, crop) {
    const f = this.cfg.fields.find((x) => x.key === key);
    if (!f || !file) return false;
    const w = f.words || {};
    const say = (t) => {
      const busy = this.querySelector(`[data-mk-f="${key}"] [data-mk-pbusy]`);
      if (!busy) return;
      busy.textContent = t;
      busy.hidden = !t;
    };
    say(w.loading || 'Opening your photo…');
    let pic = null;
    try {
      pic = await decodePhoto(file);
    } catch (e) {
      pic = null;
    }
    if (!pic) {
      say(w.unreadable || 'That photo would not open here. JPEG and PNG work everywhere.');
      return false;
    }
    const S = this.S;
    const old = this.photos[key];
    if (old && old.own && old.src) URL.revokeObjectURL(old.src);
    this.photos[key] = { own: true, file, w: pic.w, h: pic.h, src: pic.src, name: file.name || 'Your photo' };
    S.v[key] = pic.src;
    const c = crop || f.ownCrop || [50, 40, 1];
    S.v[key + 'X'] = c[0];
    S.v[key + 'Y'] = c[1];
    S.v[key + 'Zoom'] = c[2];
    this.typed.add(key);
    this.#clearAsk();
    this.#drawFields();
    this.render();
    return true;
  }

  // A basket line being edited: the photo comes back from the file on the line, if the browser
  // can fetch it. Otherwise the customer is asked for it again.
  async #photosFromLine(props) {
    for (const f of this.cfg.fields.filter((x) => x.type === 'photo')) {
      const key = f.fileProp || PHOTO_PROP;
      const url = [key, ...oldPhotoProps(key)].map((k) => props[k]).find(Boolean);
      let ok = false;
      if (typeof url === 'string' && /^(https?:)?\/\//.test(url)) {
        try {
          const r = await fetch(url);
          if (r.ok) {
            const blob = await r.blob();
            const name = decodeURIComponent(url.split('?')[0].split('/').pop() || 'photo.jpg');
            const file = new File([blob], name, { type: blob.type || 'image/jpeg' });
            ok = await this.#photoLoad(f.key, file, this.crops && this.crops[f.key]);
          }
        } catch (e) {
          ok = false;
        }
      }
      if (!ok && props._design) this.#ask((f.words && f.words.again) || 'We could not bring your photo back from the basket. Please upload it again.');
    }
  }

  // Dragging the photo on the preview moves it in its frame; the board is drawn again where it
  // lands
  #panBind() {
    this.board.querySelectorAll('img[data-pan]').forEach((img) => {
      const key = img.dataset.pan;
      if (!this.cfg.fields.some((x) => x.key === key && x.type === 'photo') && !(/^ai\d*$/.test(key) && this.cfg.ai)) return;
      img.onpointerdown = (e) => {
        if (e.button !== 0) return;
        const S = this.S;
        const box = img.parentElement.getBoundingClientRect();
        const nw = img.naturalWidth || 1;
        const nh = img.naturalHeight || 1;
        const z = Number(S.v[key + 'Zoom']) || 1;
        const s0 = Math.max(box.width / nw, box.height / nh);
        // The photo moves by its overhang at this zoom across the whole of object-position
        const spanX = nw * s0 * z - box.width;
        const spanY = nh * s0 * z - box.height;
        const start = { x: e.clientX, y: e.clientY, px: Number(S.v[key + 'X']), py: Number(S.v[key + 'Y']) };
        let moved = null;
        try {
          img.setPointerCapture(e.pointerId);
        } catch (err) {
          /* the drag still works while the pointer stays on the photo */
        }
        img.classList.add('is-grabbing');
        e.preventDefault();
        img.onpointermove = (ev) => {
          const px = spanX > 0.5 ? Math.min(100, Math.max(0, start.px - ((ev.clientX - start.x) / spanX) * 100)) : start.px;
          const py = spanY > 0.5 ? Math.min(100, Math.max(0, start.py - ((ev.clientY - start.y) / spanY) * 100)) : start.py;
          moved = [px, py];
          img.style.objectPosition = `${px.toFixed(2)}% ${py.toFixed(2)}%`;
          img.style.transformOrigin = `${px.toFixed(2)}% ${py.toFixed(2)}%`;
        };
        const end = () => {
          img.onpointermove = null;
          img.onpointerup = img.onpointercancel = null;
          img.classList.remove('is-grabbing');
          if (!moved) return;
          S.v[key + 'X'] = +moved[0].toFixed(2);
          S.v[key + 'Y'] = +moved[1].toFixed(2);
          this.render();
        };
        img.onpointerup = end;
        img.onpointercancel = end;
      };
    });
  }

  // The print check: the customer's photo at the chosen size (Large until one is chosen), in
  // dots per inch across its frame. Under 150 a yellow note says so, and they can still order.
  photoDpi(key) {
    const ph = this.photos[key];
    const img = this.board && this.board.querySelector(`img[data-pan="${key}"]`);
    const art = this.board && this.board.querySelector('.btf-mk-board__art');
    if (!ph || !ph.own || !img || !art || !ph.w) return 0;
    const box = img.parentElement.getBoundingClientRect();
    const A = art.getBoundingClientRect();
    if (!box.width || !A.width) return 0;
    const z = Number(this.S.v[key + 'Zoom']) || 1;
    const s0 = Math.max(box.width / ph.w, box.height / ph.h);
    return Math.round((A.width * 2.54) / (s0 * z * this.cm()[0]));
  }

  #photoCheck() {
    this.querySelectorAll('[data-mk-pzv]').forEach((zv) => (zv.textContent = Number(this.S.v[zv.dataset.mkPzv + 'Zoom']).toFixed(2) + '×'));
    this.cfg.fields.filter((f) => f.type === 'photo').forEach((f) => {
      const note = this.querySelector(`[data-mk-pnote="${f.key}"]`);
      if (!note) return;
      const w = f.words || {};
      const size = this.S.size || 'Large';
      // An illustrated style prints the drawing, not the photo, so the photo's print check is moot
      const dpi = this.cfg.ai && this.#aiAll().includes(f.key) && this.cfg.ai.isAi(this.S.v) ? 0 : this.photoDpi(f.key);
      let html = '';
      let cls = '';
      if (!this.photos[f.key] || !this.photos[f.key].own) {
        cls = ' is-sample';
        html = `<b>${esc(w.sampleNote || 'This is our sample photo.')}</b><span>${esc(w.sampleWhy || 'Upload your own to order your board.')}</span>`;
      } else if (!dpi && this.cfg.ai && this.cfg.ai.smallPhoto && this.#aiAll().includes(f.key) && this.cfg.ai.isAi(this.S.v) && Math.min(this.photos[f.key].w, this.photos[f.key].h) < (this.cfg.ai.minSide || 1000)) {
        // An illustrated style, from a small photo: a friendly word before a try is spent, as the
        // drawing (and cutting it out) suffers most from small photos (Duncan 17 Oct 2026)
        const t = this.cfg.ai.smallPhoto(this.S.v, f.key, this.photos[f.key]);
        cls = ' is-warn';
        html = `<b>${esc(t.title)}</b><span>${esc(t.text)}</span>${t.tips && t.tips.length ? `<ul class="btf-mk-photo__tips">${t.tips.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}`;
      } else if (dpi && dpi < 150) {
        cls = ' is-warn';
        html = `<b>May print soft at ${esc(size)}.</b><span>This photo prints at about ${dpi} dpi in this frame. We recommend 150 or more: zoom out a little, choose a smaller size, or try a larger photo. You can still order, and we will point it out on your proof.</span>`;
      } else if (dpi) {
        html = `<b>Sharp at ${esc(size)}.</b><span>Prints at about ${dpi} dpi in this frame.</span>`;
      }
      note.className = 'btf-mk-photo__note' + cls;
      if (note.innerHTML !== html) note.innerHTML = html;
      note.hidden = !html;
    });
  }

  #drawFields() {
    const box = this.querySelector('[data-mk-fields]');
    if (!box) return;
    // A choice that sits above the hero heading (the pet) is drawn there instead
    const top = document.querySelector('[data-mk-herotop]');
    const up = top ? this.cfg.fields.filter((f) => f.hero) : [];
    if (top) {
      top.innerHTML = up.map((f) => this.#fieldHtml(f)).join('');
      top.hidden = !up.length;
    }
    box.innerHTML = this.cfg.fields.filter((f) => this.visible(f) && !f.hidden && !up.includes(f)).map((f) => this.#fieldHtml(f)).join('');
    this.querySelectorAll('[data-mk-tal]').forEach((b) => b.classList.toggle('is-on', b.dataset.mkTal === (this.S.tal || this.board.querySelector('.btf-mk-board__art')?.dataset.tal0)));
  }

  #drawRoomTabs() {
    this.querySelectorAll('[data-mk-room]').forEach((t) => {
      const on = t.dataset.mkRoom === this.S.room;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-pressed', String(on));
    });
  }

  /* ---------------------------------------------------------------- render */

  render() {
    const S = this.S;
    if (this.ai && this.ai.fit) requestAnimationFrame(() => this.#aiFit(0));
    // The share strip makes its pictures again once the board settles (a drawing arriving changes
    // the board without any input from the customer: the share sent the photo, Duncan 16 Oct 2026)
    queueMicrotask(() => this.dispatchEvent(new CustomEvent('btf:redraw', { bubbles: true })));
    // Anonymous counts (assets/btf-count.js): what the customer moves to, never who they are.
    // Not on the studio print page, which rebuilds boards out of sight
    if (window.btfTrack && !this.hasAttribute('data-studio')) {
      const v = S.v || {};
      const bgName = this.cfg.fields.find((f) => f.key === 'bg' && f.type === 'choice');
      const opt = bgName && (bgName.options || []).find((o) => o[0] === v.bg);
      window.btfTrack(this, this.cfg.key, {
        design: this.L && (this.L.name || this.L.id),
        colour: this.C && this.C.name,
        size: S.size || '',
        format: this.O === 'land' ? 'Landscape' : this.O === 'pano' ? 'Panoramic' : 'Portrait',
        style: v.style || '',
        pet: v.pet || '',
        background: opt ? opt[1] : '',
      });
    }
    // Preview
    this.paint(this.board, this.#current({ interactive: true }));
    this.#panBind();
    this.#photoCheck();
    const zm = this.querySelector('[data-mk-zm]');
    if (zm) zm.hidden = !(this.cfg.zoom && this.board.querySelector('svg.btf-mk-map'));
    const credits = this.querySelector('[data-mk-credits]');
    if (credits && this.cfg.credits && credits.hidden) {
      credits.textContent = this.cfg.credits;
      credits.hidden = false;
    }
    const note = this.querySelector('[data-mk-dragnote]');
    if (note) {
      note.textContent = this.cfg.dragNote || '';
      note.hidden = !(this.cfg.dragNote && this.board.querySelector('svg.btf-mk-map'));
    }
    this.querySelector('[data-mk-stage]')?.style.setProperty('--mk-max', this.O === 'land' ? '520px' : '380px');
    // Cards: each is redrawn as the words change, its own format and colour kept
    this.querySelectorAll('[data-mk-mini]').forEach((el) => {
      const i = Number(el.dataset.mkMini);
      const sample = this.#sample(i);
      // A card is drawn again only when what it shows has changed
      const key = JSON.stringify([sample.colour.name, sample.format, sample.values]);
      if (el.dataset.drawn !== key) {
        el.dataset.drawn = key;
        this.paint(el, sample);
      }
      el.closest('.btf-mk-card').classList.toggle('is-on', i === S.layout);
      el.closest('.btf-mk-card').querySelector('[data-mk-pick]').setAttribute('aria-pressed', String(i === S.layout));
    });
    // Type controls
    const art = this.board.querySelector('.btf-mk-board__art');
    const type = this.querySelector('[data-mk-type]');
    if (type) {
      type.hidden = art && art.dataset.notype === '1';
      const cur = S.tal || (art && art.dataset.tal0);
      this.querySelectorAll('[data-mk-tal]').forEach((b) => {
        b.classList.toggle('is-on', b.dataset.mkTal === cur);
        b.setAttribute('aria-pressed', String(b.dataset.mkTal === cur));
      });
      const alignWrap = this.querySelector('[data-mk-alignwrap]');
      if (alignWrap) alignWrap.hidden = !!this.L.noAlign;
      const tv = this.querySelector('[data-mk-tszv]');
      if (tv) tv.textContent = S.tsz + '%';
    }
    // Caps
    this.querySelectorAll('[data-mk-caps]').forEach((b) => {
      const on = b.dataset.mkCaps === this.caps;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    this.#rooms();
    this.#minis();
    this.#aiDraw();
    this.#summary();
    this.#price();
    if (this.#lb.open) this.#lbDraw();
    if (this.share && this.share.prepare) this.share.prepare();
    if (this.stickGo) this.stickGo();
  }

  // Words on the page that follow a choice ("Your {pet}, on the wall"): any element with
  // data-mk-tpl, in the hero or the steps, filled from the settings module's words()
  #words() {
    if (!this.cfg.words) return;
    const w = this.cfg.words(this.S.v) || {};
    document.querySelectorAll('[data-mk-tpl]').forEach((el) => {
      const text = esc(el.dataset.mkTpl.replace(/\{(\w+)\}/g, (m, k) => (k in w ? w[k] : m)));
      el.innerHTML = el.hasAttribute('data-mk-br') ? text.replace(', ', ',<br>') : text;
    });
  }

  // A choice changed (the pet): the values that follow it, the fields' labels and the page's
  // words with it
  #choose(key, value) {
    const f = this.cfg.fields.find((x) => x.key === key && x.type === 'choice');
    if (!f || !this.#option(f, value)) return;
    this.S.v[key] = value;
    this.typed.add(key);
    this.#follow();
    // The design's own words follow the choice too (each pet's starting words), and a design the
    // choice does not offer gives way to the first one that it does
    this.#defaults(this.S.layout);
    if (!this.ok(this.S.layout)) this.#switch(this.okList()[0] || 0);
    this.#coerce();
    this.#drawCards();
    this.querySelectorAll('[data-mk-mini]').forEach((el) => delete el.dataset.drawn);
    if (this.cfg.changed) this.cfg.changed(key, this);
    // An illustrated style chosen: the customer's sign-in and tries are looked up
    if (this.cfg.ai && this.cfg.ai.isAi(this.S.v)) this.#aiSession();
    const back = document.activeElement && document.activeElement.matches('[data-mk-choice]');
    this.#drawFields();
    this.#words();
    this.render();
    if (this.heroEl) this.#heroDraw();
    if (back) document.querySelector(`[data-mk-choice="${key}"][data-mk-val="${CSS.escape(value)}"]`)?.focus();
  }

  #fitAll() {
    // Fonts arriving change the words' widths, so every board is drawn and fitted again
    this.querySelectorAll('[data-mk-mini]').forEach((el) => delete el.dataset.drawn);
    this.render();
    if (this.share && this.share.prepare) this.share.prepare();
  }

  /* ---------------------------------------------------------- collapsing */

  // Step 1 shrinks to a strip once a design is chosen; Steps 2 and 3 once a size and colour are
  #minis() {
    const S = this.S;
    const s1 = this.querySelector('[data-mk-s1]');
    const min1 = this.querySelector('[data-mk-s1min]');
    if (s1 && min1 && this.cfg.collapse1 !== false) {
      const on = this.touched.s1 && !s1.classList.contains('is-open');
      s1.classList.toggle('is-min', on);
      if (on) {
        min1.innerHTML = `
          <span class="btf-mk-min__pick"><span class="btf-mk-lbl">${esc((this.cfg.designWords || [])[0] || 'Design')}</span><b>${String(this.okList().indexOf(S.layout) + 1).padStart(2, '0')} · ${esc(this.lname())}</b></span>
          <span class="btf-mk-min__icons">${this.okList().map((i, j) => { const l = this.cfg.layouts[i]; return `<button type="button" class="btf-mk-min__icon${i === S.layout ? ' is-on' : ''}" data-mk-pick="${i}" aria-label="${esc(this.lname(l))}" aria-pressed="${i === S.layout}" style="background: ${this.colours(l)[this.showCw(l)].bg}; color: ${this.colours(l)[this.showCw(l)].ink};">${j + 1}</button>`; }).join('')}</span>
          <button type="button" class="btf-mk-min__open" data-mk-open="s1">Show all ${esc((this.cfg.designWords || [])[1] || 'designs')}</button>`;
      }
    }
    const r23 = this.querySelector('[data-mk-r23]');
    const min23 = this.querySelector('[data-mk-r23min]');
    if (r23 && min23 && this.cfg.collapse23 !== false) {
      const on = this.touched.s3 && S.size && !r23.classList.contains('is-open');
      r23.classList.toggle('is-min', !!on);
      min23.hidden = !on;
      if (on) {
        const cm = this.cm();
        min23.innerHTML = `
          <span class="btf-mk-min__pick"><span class="btf-mk-lbl">Format and size</span><b>${this.O === 'land' ? 'Landscape' : 'Portrait'} · ${S.size}, ${cm[0]} × ${cm[1]} cm</b></span>
          <span class="btf-mk-min__pick"><span class="btf-mk-lbl">Colour</span><b><i class="btf-mk-min__chip" style="background: ${this.C.bg};"></i>${esc(this.C.name)}</b></span>
          <button type="button" class="btf-mk-min__open" data-mk-open="r23">Change</button>`;
      }
    }
  }

  /* ---------------------------------------------------------------- rooms */

  #watchRooms() {
    const stage = this.querySelector('[data-mk-room-stage]');
    if (!stage || !('IntersectionObserver' in window)) {
      this.#roomsInView = true;
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this.#roomsInView = true;
        io.disconnect();
        this.#rooms();
      }
    }, { rootMargin: '600px 0px' });
    io.observe(stage);
  }

  /**
   * Lays a board into a room as Quotes and sayings does: a Large board under the photograph,
   * which is multiplied over it; Medium and Small on the empty wall at their true size, with the
   * photograph's board surface over them; a gloss pass; the caps at their physical size.
   */
  roomInto(stage, room, design, frame = 3 / 2, load = true, zoom = 1, crop = null) {
    const size = design.size || 'Large';
    this.roomMod.roomLayout(stage, {
      room,
      zoom,
      crop,
      cm: this.cm(size, design.format),
      port: design.format === 'port',
      large: size === 'Large',
      frame,
      caps: design.caps,
      load,
      urls: this.setup.rooms[room],
      capUrls: { white: this.setup.assets.capWhite, black: this.setup.assets.capBlack },
    });
    const art = stage.querySelector('[data-mk-room-board]');
    if (art) this.paint(art, Object.assign({}, design, { caps: null, lite: true }));
  }

  #rooms() {
    const stage = this.querySelector('[data-mk-room-stage]');
    if (!stage) return;
    this.roomInto(stage, this.S.room, this.#current(), 3 / 2, this.#roomsInView);
    const note = this.querySelector('[data-mk-room-note]');
    if (note) note.hidden = !!this.S.size;
  }

  /* -------------------------------------------------------------- summary */

  #summary() {
    const S = this.S;
    const rows = this.querySelector('[data-mk-rows]');
    if (!rows) return;
    const cm = this.cm();
    const key = (this.cfg.summaryKeys || []).find((k) => this.cfg.fields.some((f) => f.key === k && this.visible(f)));
    const field = key && this.cfg.fields.find((f) => f.key === key);
    const list = [
      [(this.cfg.designWords || [])[0] || 'Design', esc(this.lname()), 'mk-step-1'],
      ['Format', this.O === 'land' ? 'Landscape' : 'Portrait', 'mk-step-2'],
      ['Size', S.size ? `${S.size}, ${cm[0]} × ${cm[1]} cm` : `<span class="btf-mk-rows__ask">${esc(rows.dataset.ask || 'Choose a size')}</span>`, 'mk-step-2'],
      ['Colour', esc(this.C.name), 'mk-step-3'],
    ];
    this.cfg.fields.filter((f) => f.type === 'photo' && this.visible(f)).forEach((f) => {
      const own = this.photos[f.key] && this.photos[f.key].own;
      const w = f.words || {};
      list.push([w.row || 'Photo', own ? esc(w.rowOwn || 'Your photo') : `<span class="btf-mk-rows__ask">${esc(w.rowAsk || 'Upload your photo')}</span>`, 'mk-step-4']);
    });
    if (this.cfg.ai) {
      const w = this.cfg.ai.words;
      const slots = this.#aiSlots();
      slots.forEach((slot, si) => {
        const d = this.#aiDraw0(slot);
        const label = slots.length > 1 && w.slotLabel ? w.rowLabel + ' · ' + w.slotLabel(S.v, slot, si) : w.rowLabel;
        list.push([label, !this.cfg.ai.isAi(S.v) ? esc(w.rowRaw) : d ? esc(w.rowDone(d.ver + 1)) : `<span class="btf-mk-rows__ask">${esc(w.rowTodo)}</span>`, this.cfg.ai.isAi(S.v) ? 'mk-ai' : 'mk-step-4']);
      });
    }
    if (field) list.push([this.label(field), esc(S.v[key] || '—'), 'mk-step-4']);
    list.push(['Screw caps', this.caps === 'black' ? 'Black' : 'White', 'mk-caps']);
    rows.innerHTML = list
      .map(([k, v, to]) => `<div class="btf-mk-rows__row"><dt class="btf-mk-lbl">${esc(k)}</dt><dd>${v}</dd><a href="#${to}" class="btf-mk-rows__change" data-mk-go="${to}">Change<span class="visually-hidden"> ${esc(k.toLowerCase())}</span></a></div>`)
      .join('');
  }

  #variant() {
    return this.S.size ? this.variants.find((v) => v.band === this.S.size) : null;
  }

  #priceText() {
    const v = this.#variant();
    return v ? v.price : this.setup.from;
  }

  #price() {
    const v = this.#variant();
    this.querySelectorAll('[data-mk-price]').forEach((el) => (el.textContent = v ? v.price : this.setup.from));
    const id = this.querySelector('[data-mk-variant]');
    if (id) id.value = v ? v.id : '';
  }

  /* --------------------------------------------------------------- events */

  #listen() {
    this.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const S = this.S;
      let hit;
      if ((hit = t.closest('[data-mk-toggle]'))) {
        const key = hit.dataset.mkToggle;
        S.v[key] = hit.dataset.mkVal === '1';
        this.typed.add(key);
        this.#drawFields();
        this.render();
        return;
      }
      if ((hit = t.closest('[data-mk-choice]'))) {
        this.#choose(hit.dataset.mkChoice, hit.dataset.mkVal);
        return;
      }
      if ((hit = t.closest('[data-mk-aigo]'))) {
        this.#aiGo(hit.dataset.mkAigo || undefined);
        return;
      }
      if ((hit = t.closest('[data-mk-aimore]'))) {
        this.ai.showAll = !this.ai.showAll;
        this.#aiDraw();
        return;
      }
      if ((hit = t.closest('[data-mk-aiboth]'))) {
        this.#aiBoth();
        return;
      }
      if ((hit = t.closest('[data-mk-aitarget]'))) {
        this.ai.target = hit.dataset.mkAitarget;
        this.#aiDraw();
        return;
      }
      if ((hit = t.closest('[data-mk-aidel]'))) {
        this.ai.confirm = Number(hit.dataset.mkAidel);
        this.#aiDraw();
        return;
      }
      if ((hit = t.closest('[data-mk-aidelall]'))) {
        this.ai.confirm = 'all';
        this.#aiDraw();
        return;
      }
      if ((hit = t.closest('[data-mk-aidelno]'))) {
        this.ai.confirm = null;
        this.#aiDraw();
        return;
      }
      if ((hit = t.closest('[data-mk-aidelyes]'))) {
        this.#aiDelete(hit.dataset.mkAidelyes === 'all' ? 'all' : Number(hit.dataset.mkAidelyes));
        return;
      }
      if ((hit = t.closest('[data-mk-aihist]'))) {
        this.#aiUse(Number(hit.dataset.mkAihist), hit.dataset.mkAislot || undefined);
        return;
      }
      if ((hit = t.closest('[data-mk-aiver]'))) {
        this.#aiPick(Number(hit.dataset.mkAiver), hit.dataset.mkAislot || undefined);
        return;
      }
      if ((hit = t.closest('[data-mk-recentre]'))) {
        const key = hit.dataset.mkRecentre;
        S.v[key + 'X'] = 50;
        S.v[key + 'Y'] = 50;
        this.render();
        return;
      }
      if ((hit = t.closest('[data-mk-pick]'))) {
        this.#pick(Number(hit.dataset.mkPick));
      } else if ((hit = t.closest('[data-mk-enlarge]'))) {
        this.#lbOpen('cards', Number(hit.dataset.mkEnlarge));
      } else if ((hit = t.closest('[data-mk-full]'))) {
        this.#lbOpen('full', 0);
      } else if ((hit = t.closest('[data-mk-fmt]'))) {
        S.fmt = hit.dataset.mkFmt;
        this.#drawFormats();
        this.#drawSizes();
        this.render();
      } else if ((hit = t.closest('[data-mk-size]'))) {
        S.size = hit.dataset.mkSize;
        this.#drawSizes();
        this.#clearAsk();
        this.render();
      } else if ((hit = t.closest('[data-mk-cw]'))) {
        S.cw = Number(hit.dataset.mkCw);
        this.touched.s3 = true;
        this.querySelector('[data-mk-r23]')?.classList.remove('is-open');
        this.#drawColours();
        this.render();
      } else if ((hit = t.closest('[data-mk-caps]'))) {
        S.caps = hit.dataset.mkCaps;
        this.touched.caps = true;
        this.render();
      } else if ((hit = t.closest('[data-mk-tal]'))) {
        S.tal = hit.dataset.mkTal;
        this.render();
      } else if ((hit = t.closest('[data-mk-room]'))) {
        S.room = hit.dataset.mkRoom;
        this.#drawRoomTabs();
        this.#rooms();
      } else if ((hit = t.closest('[data-mk-open]'))) {
        const which = hit.dataset.mkOpen;
        this.querySelector(which === 's1' ? '[data-mk-s1]' : '[data-mk-r23]')?.classList.add('is-open');
        this.#minis();
        this.#goTo(which === 's1' ? 'mk-step-1' : 'mk-step-2');
      } else if ((hit = t.closest('[data-mk-go]'))) {
        e.preventDefault();
        const to = hit.dataset.mkGo;
        if (to === 'mk-step-1') this.querySelector('[data-mk-s1]')?.classList.add('is-open');
        if (to === 'mk-step-2' || to === 'mk-step-3') this.querySelector('[data-mk-r23]')?.classList.add('is-open');
        this.#minis();
        this.#goTo(to);
      } else if ((hit = t.closest('[data-mk-option]'))) {
        this.#placeChoose(hit);
      } else if ((hit = t.closest('[data-mk-mz]'))) {
        if (this.cfg.zoom) this.cfg.zoom(this, Number(hit.dataset.mkMz) * 0.5);
      }
    });

    this.addEventListener('input', (e) => {
      const t = e.target;
      if (t.matches('[data-mk-k]')) {
        const f = this.cfg.fields.find((x) => x.key === t.dataset.mkK);
        if (!f) return;
        if ((f.type === 'date' || f.type === 'time') && !t.value) return;
        this.S.v[f.key] = t.value;
        this.typed.add(f.key);
        if (this.cfg.changed) this.cfg.changed(f.key, this);
        const count = this.querySelector(`[data-mk-count="${f.key}"]`);
        if (count) count.textContent = t.value.length + ' / ' + f.max;
        this.render();
      } else if (t.matches('[data-mk-tsz]')) {
        this.S.tsz = Number(t.value);
        this.render();
      } else if (t.matches('[data-mk-pz]')) {
        this.S.v[t.dataset.mkPz + 'Zoom'] = Number(t.value);
        this.render();
      } else if (t.matches('[data-mk-range]')) {
        const k = t.dataset.mkRange;
        const f = this.cfg.fields.find((x) => x.key === k);
        this.S.v[k] = Number(t.value);
        const rv = this.querySelector(`[data-mk-rangev="${k}"]`);
        if (rv) rv.textContent = t.value + ((f && f.unit) || '');
        this.render();
      } else if (t.matches('[data-mk-placein]')) {
        this.#placeSearch(t);
      }
    });

    // A choice drawn above the hero heading, outside this element (the pet)
    document.querySelector('[data-mk-herotop]')?.addEventListener('click', (e) => {
      const hit = e.target instanceof Element && e.target.closest('[data-mk-choice]');
      if (hit) this.#choose(hit.dataset.mkChoice, hit.dataset.mkVal);
    });

    // The customer's photo: chosen, or dropped on its field
    this.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches && t.matches('[data-mk-photo]') && t.files && t.files[0]) {
        if (window.btfCount) window.btfCount(this.cfg.key, 'photo', '', true);
        this.#photoLoad(t.dataset.mkPhoto, t.files[0]);
      }
    });
    this.addEventListener('dragover', (e) => {
      const box = e.target.closest && e.target.closest('[data-mk-drop]');
      if (!box || !e.dataTransfer || ![...e.dataTransfer.types].includes('Files')) return;
      e.preventDefault();
      box.classList.add('is-over');
    });
    this.addEventListener('dragleave', (e) => {
      const box = e.target.closest && e.target.closest('[data-mk-drop]');
      if (box && !box.contains(e.relatedTarget)) box.classList.remove('is-over');
    });
    this.addEventListener('drop', (e) => {
      const box = e.target.closest && e.target.closest('[data-mk-drop]');
      if (!box || !e.dataTransfer || !e.dataTransfer.files.length) return;
      e.preventDefault();
      box.classList.remove('is-over');
      this.#photoLoad(box.dataset.mkDrop, e.dataTransfer.files[0]);
    });

    this.addEventListener('focusin', (e) => {
      if (e.target.matches && e.target.matches('[data-mk-placein]')) this.data('places').catch(() => {});
    });
    this.addEventListener('keydown', (e) => {
      if (e.target.matches && e.target.matches('[data-mk-placein]')) this.#placeKeys(e);
      if (this.#lb.open) this.#lbKeys(e);
    });
    this.addEventListener('focusout', (e) => {
      const box = e.target.closest && e.target.closest('[data-mk-place]');
      if (box && !box.contains(e.relatedTarget)) setTimeout(() => this.#placeClose(box), 150);
    });
  }

  #pick(i) {
    if (i < 0 || i >= this.cfg.layouts.length || !this.ok(i)) return;
    this.touched.s1 = true;
    this.#switch(i);
    this.querySelector('[data-mk-s1]')?.classList.remove('is-open');
    this.render();
    if (this.cfg.collapse1 !== false) this.#goTo('mk-step-2', false);
  }

  // The design changes: its own words, format and options
  #switch(i) {
    const S = this.S;
    const was = S.layout;
    const had = this.C.name;
    S.layout = i;
    this.#defaults(i);
    if (was !== i && this.cfg.showValues) {
      // The design's showcase values (the Pet portrait's style), for any the customer has not set
      const sv = this.cfg.showValues(S.v, this.L);
      Object.keys(sv).forEach((k) => !this.typed.has(k) && (S.v[k] = sv[k]));
    }
    this.#coerce();
    if (was !== i) {
      const l = this.L;
      if (l.o === 'any' && l.fmt) S.fmt = l.fmt;
      if (l.show) {
        // A colour the customer chose stays (by name, where this design has it); otherwise the
        // design opens in its showcase colour
        const keep = this.touched.s3 ? this.colours(l).findIndex((c) => c.name === had) : -1;
        S.cw = keep >= 0 ? keep : this.showCw(l);
      } else S.cw = 0;
      S.tal = '';
      this.#drawFormats();
      this.#drawSizes();
      this.#drawColours();
      this.#drawFields();
    }
  }

  #goTo(id, focus = true) {
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'start' });
    if (focus) {
      const field = target.querySelector('.is-on, input, button');
      if (field) field.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------ place search */

  async #placeSearch(input) {
    const box = input.closest('[data-mk-place]');
    const list = box.querySelector('[role="listbox"]');
    const q = fold(input.value.trim());
    if (q.length < 2) return this.#placeClose(box);
    let data;
    try {
      data = await this.data('places');
    } catch (e) {
      return;
    }
    if (fold(input.value.trim()) !== q) return;
    const [qName, qRegion] = q.split(/\s*,\s*/);
    const hits = [];
    // A UK postcode, whole or just its district, is looked up at postcodes.io (open data, no key)
    const field = this.cfg.fields.find((f) => f.key === box.dataset.mkPlace);
    const pc = q.replace(/\s+/g, '').toUpperCase();
    let pcHit = null;
    if (field && field.postcode && /^[A-Z]{1,2}\d[A-Z\d]?(\d[A-Z]{2})?$/.test(pc)) {
      const full = pc.length > 4 && /\d[A-Z]{2}$/.test(pc);
      try {
        const r = await fetch('https://api.postcodes.io/' + (full ? 'postcodes/' : 'outcodes/') + encodeURIComponent(pc));
        const j = r.ok ? await r.json() : null;
        const res = j && j.result;
        if (res && res.latitude != null && fold(input.value.trim()) === q) {
          // The board names the town: a full postcode's parish (or its district where there is
          // none); a district such as BA14 takes the nearest town in the place list
          const lat = +res.latitude.toFixed(5);
          const lon = +res.longitude.toFixed(5);
          const district = full ? res.admin_district || '' : (res.admin_district || [])[0] || '';
          let town = field.town === 'largest' ? '' : full && res.parish && !/unparished/i.test(res.parish) ? res.parish : full ? district : '';
          // A full postcode names the town people know it by: the place list's own town when one
          // lies within 2 km (BL0 9AG is Ramsbottom, not its council, Bury; CF10 1AA is Cardiff,
          // not its community, Castle), otherwise the parish, then the council (10 Oct 2026)
          if (full && field.town !== 'largest') {
            const r2 = (2 / 111) ** 2;
            let nearest = null;
            let best = Infinity;
            for (const pl of data.places) {
              const d = (pl[2] - lat) ** 2 + ((pl[3] - lon) * Math.cos((lat * Math.PI) / 180)) ** 2;
              if (d < best) {
                best = d;
                nearest = pl;
              }
            }
            if (nearest && best <= r2) town = nearest[0];
          }
          // The night sky names the town people know the place by: London for the inner London
          // postcodes, otherwise the largest town within about 5 km (the list runs largest first),
          // so a village or suburb postcode reads as its town without jumping to the next one
          if (!town && field.town === 'largest' && /^(E|EC|N|NW|SE|SW|W|WC)\d/.test(pc)) town = 'London';
          if (!town && field.town === 'largest') {
            const r2 = (5 / 111) ** 2;
            const near = data.places.find((pl) => (pl[2] - lat) ** 2 + ((pl[3] - lon) * Math.cos((lat * Math.PI) / 180)) ** 2 < r2);
            if (near) town = near[0];
          }
          if (!town) {
            let best = Infinity;
            for (const pl of data.places) {
              const d = (pl[2] - lat) ** 2 + ((pl[3] - lon) * Math.cos((lat * Math.PI) / 180)) ** 2;
              if (d < best) {
                best = d;
                town = pl[0];
              }
            }
          }
          pcHit = { name: town, region: district === town ? '' : district, lat, lon, tz: 'Europe/London', postcode: full ? res.postcode : res.outcode };
        }
      } catch (e) {
        /* fall through to the place list */
      }
    }
    for (const p of data.places) {
      const n = fold(p[0]);
      if (!n.startsWith(qName)) continue;
      if (qRegion && !fold(p[1]).startsWith(qRegion)) continue;
      hits.push(p);
      if (hits.length >= 8) break;
    }
    // Exact names first, then the list's own order (largest places first)
    hits.sort((a, b) => (fold(a[0]) === qName ? 0 : 1) - (fold(b[0]) === qName ? 0 : 1));
    const opts = hits.map((p) => ({ name: p[0], region: p[1], lat: p[2], lon: p[3], tz: data.tz[p[4]] }));
    if (pcHit) opts.unshift(pcHit);
    list.innerHTML = opts.length
      ? opts.map((p, i) => `<li role="option" id="${input.id}-o${i}" class="btf-mk-place__opt" data-mk-option='${esc(JSON.stringify(p))}'>${esc(p.postcode || p.name)}<small>${esc(p.postcode ? [p.name, p.region].filter(Boolean).join(', ') : p.region)}</small></li>`).join('')
      : `<li class="btf-mk-place__none" aria-disabled="true">${esc(box.dataset.none || 'No place by that name. Try the nearest town.')}</li>`;
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    box.dataset.active = '-1';
  }

  #placeChoose(opt) {
    const box = opt.closest('[data-mk-place]');
    const input = box.querySelector('input');
    const p = JSON.parse(opt.dataset.mkOption);
    this.S.v[box.dataset.mkPlace] = p;
    this.typed.add(box.dataset.mkPlace);
    if (this.cfg.changed) this.cfg.changed(box.dataset.mkPlace, this);
    input.value = placeLabel(p);
    this.#placeClose(box);
    this.render();
  }

  #placeClose(box) {
    const list = box.querySelector('[role="listbox"]');
    const input = box.querySelector('input');
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    // Typing without choosing puts the last chosen place back
    const v = this.S.v[box.dataset.mkPlace];
    if (v && document.activeElement !== input) input.value = placeLabel(v);
  }

  #placeKeys(e) {
    const box = e.target.closest('[data-mk-place]');
    const opts = [...box.querySelectorAll('[data-mk-option]')];
    let i = Number(box.dataset.active || -1);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!opts.length) return;
      e.preventDefault();
      i = e.key === 'ArrowDown' ? Math.min(opts.length - 1, i + 1) : Math.max(0, i - 1);
      box.dataset.active = i;
      opts.forEach((o, j) => o.classList.toggle('is-active', j === i));
      e.target.setAttribute('aria-activedescendant', opts[i].id);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (opts[Math.max(0, i)]) this.#placeChoose(opts[Math.max(0, i)]);
    } else if (e.key === 'Escape') {
      this.#placeClose(box);
    }
  }

  /* ---------------------------------------------------- enlarge, full screen */

  #lbOpen(mode, i) {
    const lb = this.querySelector('[data-mk-lb]');
    if (!lb) return;
    this.#lb = { open: true, i, mode, back: document.activeElement };
    lb.hidden = false;
    document.documentElement.classList.add('btf-mk-locked');
    this.#lbDraw();
    lb.querySelector('[data-mk-lbclose]')?.focus();
  }

  #lbClose() {
    const lb = this.querySelector('[data-mk-lb]');
    if (!lb) return;
    this.#lb.open = false;
    lb.hidden = true;
    lb.innerHTML = '';
    document.documentElement.classList.remove('btf-mk-locked');
    if (this.#lb.back && this.#lb.back.focus) this.#lb.back.focus();
  }

  #lbDraw() {
    const lb = this.querySelector('[data-mk-lb]');
    const { mode, i } = this.#lb;
    const ok = this.okList();
    const n = ok.length;
    const cards = mode === 'cards';
    const l = cards ? this.cfg.layouts[i] : this.L;
    lb.innerHTML = `
      <div class="btf-mk-lb__panel" role="dialog" aria-modal="true" aria-label="${esc(cards ? this.lname(l) : 'Your design')}">
        <div class="btf-mk-lb__head"><span class="btf-mk-lb__title">${cards ? `<span>${String(ok.indexOf(i) + 1).padStart(2, '0')} / ${String(n).padStart(2, '0')}</span> ${esc(this.lname(l))}` : 'Your design'}</span><button type="button" class="btf-mk-xb" data-mk-lbclose>${ICON.x}<span>Close</span></button></div>
        <div class="btf-mk-lb__stage">
          ${cards ? `<button type="button" class="btf-mk-lb__nav" data-mk-lbgo="-1" aria-label="Previous design">${ICON.prev}</button>` : '<span></span>'}
          <div class="btf-mk-lb__wrap"><div class="btf-mk-board" data-mk-lbboard></div></div>
          ${cards ? `<button type="button" class="btf-mk-lb__nav" data-mk-lbgo="1" aria-label="Next design">${ICON.next}</button>` : '<span></span>'}
        </div>
        ${cards ? `<div class="btf-mk-lb__foot"><span>${esc(this.ldesc(l))}</span><button type="button" class="btf-mk-btn" data-mk-lbuse>Use this design</button></div>
        <div class="btf-mk-lb__strip" style="grid-template-columns: repeat(${n}, minmax(0, 1fr));">${ok.map((j, k) => `<button type="button" class="${j === i ? 'is-on' : ''}" data-mk-lbto="${j}">${String(k + 1).padStart(2, '0')}</button>`).join('')}</div>` : ''}
      </div>`;
    const board = lb.querySelector('[data-mk-lbboard]');
    const design = cards ? Object.assign(this.#sample(i), { lite: false }) : this.#current();
    this.paint(board, design);
    board.closest('.btf-mk-lb__wrap').style.setProperty('--mk-lb-ar', this.cm('Large', design.format).join(' / '));
    lb.onclick = (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t === lb || t.closest('[data-mk-lbclose]')) return this.#lbClose();
      const go = t.closest('[data-mk-lbgo]');
      const to = t.closest('[data-mk-lbto]');
      if (go) this.#lbMove(Number(go.dataset.mkLbgo));
      else if (to) {
        this.#lb.i = Number(to.dataset.mkLbto);
        this.#lbDraw();
      }
      else if (t.closest('[data-mk-lbuse]')) {
        const j = this.#lb.i;
        this.#lbClose();
        this.#pick(j);
      }
    };
    let x0 = null;
    lb.onpointerdown = (e) => (x0 = e.clientX);
    lb.onpointerup = (e) => {
      if (x0 == null || this.#lb.mode !== 'cards') return;
      const d = e.clientX - x0;
      x0 = null;
      if (Math.abs(d) > 50) this.#lbMove(d < 0 ? 1 : -1);
    };
  }

  #lbMove(d) {
    this.#lb.i = this.#step(this.#lb.i, d);
    this.#lbDraw();
  }

  // The design d places on from i, among those this choice allows, round and round
  #step(i, d) {
    const ok = this.okList();
    if (!ok.length) return 0;
    const at = ok.indexOf(i);
    const n = ok.length;
    return ok[((((at < 0 ? 0 : at) + d) % n) + n) % n];
  }

  #lbKeys(e) {
    if (e.key === 'Escape') this.#lbClose();
    else if (this.#lb.mode === 'cards' && e.key === 'ArrowRight') this.#lbMove(1);
    else if (this.#lb.mode === 'cards' && e.key === 'ArrowLeft') this.#lbMove(-1);
    else if (e.key === 'Tab') {
      // Keep the focus inside the dialog
      const f = [...this.querySelectorAll('[data-mk-lb] button')];
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* ------------------------------------------------------------------ hero */

  #hero_init() {
    const hero = document.querySelector('[data-mk-hero]');
    if (!hero) return;
    this.heroEl = hero;
    const n = this.#slides() ? this.#slides().length : this.cfg.layouts.length;
    hero.querySelector('[data-mk-hero-prev]')?.addEventListener('click', () => this.#heroGo(-1, true));
    hero.querySelector('[data-mk-hero-next]')?.addEventListener('click', () => this.#heroGo(1, true));
    hero.querySelector('[data-mk-hero-use]')?.addEventListener('click', () => {
      const sl = this.#slides();
      const x = sl && sl[this.#hero.i];
      this.#pick(x ? this.#slideLayout(x) : this.#hero.i);
      // The slide's own words and layout come too, where the customer hasn't written their own
      if (x && x.values) {
        Object.entries(x.values).forEach(([k, val]) => {
          if (val != null && !/^ai/.test(k) && k !== 'psize' && !this.typed.has(k)) this.S.v[k] = val;
        });
        this.#drawFields();
        this.render();
      }
    });
    hero.addEventListener('mouseenter', () => (this.#hero.paused = true));
    hero.addEventListener('mouseleave', () => (this.#hero.paused = false));
    hero.addEventListener('focusin', () => (this.#hero.paused = true));
    hero.addEventListener('focusout', () => (this.#hero.paused = false));
    this.#heroDraw();
    if (n > 1) {
      this.#hero.timer = setInterval(() => {
        if (!this.#hero.paused && !document.hidden) this.#heroGo(1, false);
      }, HERO_MS);
    }
  }

  // The hero's own slides (the travel poster, Duncan 21 Oct 2026: real places, several to a
  // style): the settings module's heroSlides, each a design (layout id), its values and colour
  #slides() {
    const sl = this.cfg.heroSlides;
    return sl && sl.length ? sl.filter((x) => this.#slideLayout(x) >= 0) : null;
  }

  #slideLayout(x) {
    return this.cfg.layouts.findIndex((l) => l.id === x.layout);
  }

  #heroGo(d, user) {
    const sl = this.#slides();
    this.#hero.i = sl ? (((this.#hero.i + d) % sl.length) + sl.length) % sl.length : this.#step(this.#hero.i, d);
    if (user) {
      // The customer took over: stop moving on by itself
      clearInterval(this.#hero.timer);
    }
    this.#heroDraw();
  }

  #heroDraw() {
    const hero = this.heroEl;
    if (!hero) return;
    if (this.#slides()) return this.#heroSlide(hero);
    if (!this.ok(this.#hero.i)) this.#hero.i = this.#step(this.#hero.i, 0);
    const i = this.#hero.i;
    const ok = this.okList();
    const n = ok.length;
    const stage = hero.querySelector('[data-mk-room-stage]');
    const design = this.#sample(i);
    design.size = 'Large';
    // A hero room with only a landscape board shows every design that can be landscape in landscape
    if (hero.dataset.format === 'land' && this.cfg.layouts[i].o === 'any') design.format = 'land';
    design.caps = capFor(design.colour.bg);
    // The hero's room may be cropped in closer round the board (the section's zoom setting)
    const swap = () => this.roomInto(stage, hero.dataset.room || 'studio', design, 5 / 4, true, Number(hero.dataset.zoom) || 1, hero.dataset.crop ? hero.dataset.crop.split(',').map(Number) : null);
    if (still() || !stage.dataset.drawn) swap();
    else {
      stage.classList.add('is-fading');
      setTimeout(() => {
        swap();
        stage.classList.remove('is-fading');
      }, 220);
    }
    stage.dataset.drawn = '1';
    const count = hero.querySelector('[data-mk-hero-n]');
    if (count) count.textContent = String(ok.indexOf(i) + 1).padStart(2, '0') + ' / ' + n;
    const name = hero.querySelector('[data-mk-hero-name]');
    if (name) name.textContent = this.lname(this.cfg.layouts[i]);
    const bar = hero.querySelector('[data-mk-hero-bar]');
    if (bar) bar.style.width = pct(((ok.indexOf(i) + 1) / n) * 100);
  }

  // One of the heroSlides: its design with its own values and colour, the customer's words kept out
  #heroSlide(hero) {
    const sl = this.#slides();
    const n = sl.length;
    const at = this.#hero.i % n;
    const x = sl[at];
    const i = this.#slideLayout(x);
    const l = this.cfg.layouts[i];
    const cols = this.colours(l);
    const ci = x.colour ? cols.findIndex((c) => c.name === x.colour) : -1;
    const design = {
      layout: l,
      colour: cols[ci >= 0 ? ci : this.showCw(l)],
      format: x.format || (l.o === 'any' ? l.fmt || this.cfg.fmt : l.o),
      values: Object.assign({}, this.values(Object.assign({ tsz: 100, tal: '' }, this.cfg.showValues ? this.cfg.showValues(this.S.v, l) : {})), x.values || {}),
      size: 'Large',
      caps: null,
      lite: true,
    };
    if (hero.dataset.format === 'land' && l.o === 'any') design.format = 'land';
    design.caps = capFor(design.colour.bg);
    const stage = hero.querySelector('[data-mk-room-stage]');
    const swap = () => this.roomInto(stage, hero.dataset.room || 'studio', design, 5 / 4, true, Number(hero.dataset.zoom) || 1, hero.dataset.crop ? hero.dataset.crop.split(',').map(Number) : null);
    if (still() || !stage.dataset.drawn) swap();
    else {
      stage.classList.add('is-fading');
      setTimeout(() => {
        swap();
        stage.classList.remove('is-fading');
      }, 220);
    }
    stage.dataset.drawn = '1';
    const count = hero.querySelector('[data-mk-hero-n]');
    if (count) count.textContent = String(at + 1).padStart(2, '0') + ' / ' + n;
    const name = hero.querySelector('[data-mk-hero-name]');
    if (name) name.textContent = x.name || this.lname(l);
    const bar = hero.querySelector('[data-mk-hero-bar]');
    if (bar) bar.style.width = pct(((at + 1) / n) * 100);
  }

  /* ------------------------------------------------- the cards' colours */

  // While Step 1 is open and no design is chosen yet, each card drifts through a few colours: its
  // showcase colour, then three from the settings module's `cycle`, each card on its own beat. The
  // board's colours are custom properties (--bg, --ink, --acc), so a change is a cross-fade of
  // colour alone (the registered --mk-c* properties, assets/btf-maker.css): nothing is laid out or
  // drawn again. Off screen, in a hidden tab or with the enlarged view open it rests; once a design
  // is chosen it fades back to the showcase colours and stops for good.
  #cycleInit() {
    const box = this.querySelector('[data-mk-cards]');
    if (!this.cfg.cycle || !box || still() || this.touched.s1) return;
    const c = this.#cyc;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((es) => (c.seen = es[es.length - 1].isIntersecting)).observe(box);
    } else c.seen = true;
    c.timer = setInterval(() => this.#cycleTick(), 400);
  }

  // A card's colours, in the order it shows them
  #cycleSeq(l, pos) {
    const all = this.colours(l);
    const first = all[this.showCw(l)];
    const pool = this.cfg.cycle.map((n) => all.find((x) => x.name === n)).filter((x) => x && x !== first);
    const n = pool.length;
    const seq = [first];
    [1, 6, 11].forEach((k) => {
      const x = pool[(pos * 4 + k) % n];
      if (x && !seq.includes(x)) seq.push(x);
    });
    return seq;
  }

  #cycleSet(el, c) {
    el.style.setProperty('--mk-cbg', c.bg);
    el.style.setProperty('--mk-cink', c.ink);
    el.style.setProperty('--mk-cacc', c.acc);
  }

  #cycleTick() {
    const c = this.#cyc;
    const s1 = this.querySelector('[data-mk-s1]');
    if (this.touched.s1 || !s1 || s1.classList.contains('is-min') || still()) return this.#cycleStop();
    if (!c.seen || document.hidden || this.#lb.open) {
      c.rest = true;
      return;
    }
    const now = performance.now();
    const PERIOD = 3600;
    c.cards.forEach((st, el) => !el.isConnected && c.cards.delete(el));
    this.querySelectorAll('[data-mk-mini]').forEach((el, pos) => {
      const i = Number(el.dataset.mkMini);
      // The chosen design's card shows the customer's own colour
      if (i === this.S.layout) return;
      let st = c.cards.get(el);
      // A card drawn again (paint sets its colours afresh) starts over from its showcase colour
      if (!st || el.style.getPropertyValue('--bg').trim().indexOf('var(') !== 0) {
        st = { seq: this.#cycleSeq(this.cfg.layouts[i], pos), k: 0, next: now + 1500 + ((pos * 1530) % PERIOD), armed: false };
        c.cards.set(el, st);
        this.#cycleSet(el, st.seq[0]);
        el.style.setProperty('--bg', 'var(--mk-cbg)');
        el.style.setProperty('--ink', 'var(--mk-cink)');
        el.style.setProperty('--acc', 'var(--mk-cacc)');
        el.classList.remove('is-cyc');
        return;
      }
      // The cross-fade is switched on a beat after the colours are first set, so they arrive
      // without fading in from nothing
      if (!st.armed) {
        el.classList.add('is-cyc');
        st.armed = true;
      }
      if (c.rest) st.next = Math.max(st.next, now + 600 + ((pos * 530) % 2400));
      if (now < st.next || st.seq.length < 2) return;
      st.k = (st.k + 1) % st.seq.length;
      st.next = now + PERIOD;
      this.#cycleSet(el, st.seq[st.k]);
    });
    c.rest = false;
  }

  #cycleStop() {
    const c = this.#cyc;
    clearInterval(c.timer);
    c.timer = 0;
    c.cards.forEach((st, el) => {
      if (el.isConnected && el.style.getPropertyValue('--bg').trim().indexOf('var(') === 0) this.#cycleSet(el, st.seq[0]);
    });
    c.cards.clear();
  }

  /* ------------------------------------------------- the preview in view */

  // On a computer the preview column sticks while Step 4 is filled in (assets/btf-maker.css,
  // .btf-mk-pv__stick). The board is scaled so the stage, the Illustrate box and the caps all fit
  // the window's height below the sticky top; on a phone the column is not sticky and nothing is
  // changed.
  #stickInit() {
    const stick = this.querySelector('[data-mk-stick]');
    if (!stick) return;
    let raf = 0;
    const go = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.#stickFit());
    };
    window.addEventListener('resize', go);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(go);
      [...stick.children].forEach((n) => !n.matches('[data-mk-stage]') && ro.observe(n));
    }
    this.stickGo = go;
  }

  #stickFit() {
    const stick = this.querySelector('[data-mk-stick]');
    const stage = this.querySelector('[data-mk-stage]');
    if (!stick || !stage) return;
    const cs = getComputedStyle(stick);
    if (cs.position !== 'sticky') {
      stage.style.removeProperty('--mk-fitw');
      stage.style.removeProperty('--mk-stage-min');
      return;
    }
    const top = parseFloat(cs.top) || 0;
    const ss = getComputedStyle(stage);
    const pad = parseFloat(ss.paddingTop) + parseFloat(ss.paddingBottom);
    const others = stick.offsetHeight - stage.offsetHeight;
    // The preview never shrinks below about half the window, however much sits under it (a long
    // Your portraits strip): past that the column runs on below the fold instead
    const room = Math.max(window.innerHeight - top - 16 - others, Math.round(window.innerHeight * 0.55));
    const [w, h] = this.cm();
    const fit = Math.max(180, ((room - pad) * w) / h);
    stage.style.setProperty('--mk-fitw', Math.round(fit) + 'px');
    stage.style.setProperty('--mk-stage-min', Math.max(240, Math.min(520, Math.round(room))) + 'px');
  }

  /* ------------------------------------------------- illustrated portraits */

  #aiMod() {
    if (!this.aiModP) this.aiModP = import(this.setup.assets.ai);
    return this.aiModP;
  }

  // The photos an illustrated style draws: every photo field the settings module names (ai.photos,
  // or its one ai.photo), and those the design shows (ai.slots: Best friends draws both pets).
  // Each has its own drawings, versions and framing: 'ai' for the first, then 'ai2' and on.
  #aiAll() {
    const ai = this.cfg.ai;
    return ai.photos || [ai.photo];
  }

  #aiSlots(v = this.S.v, l = this.L) {
    const ai = this.cfg.ai;
    const all = this.#aiAll();
    return ai.slots ? ai.slots(v, l).filter((k) => all.includes(k)) : [ai.photo];
  }

  #sfx(slot) {
    return slot === this.cfg.ai.photo ? '' : String(this.#aiAll().indexOf(slot) + 1);
  }

  // Whether a picture can be made for a slot: its photo is in, or, for a maker that paints from
  // words alone (ai.text: the Travel poster), the words it needs are filled in (ai.ready)
  #aiCan(slot, v = this.S.v) {
    const ai = this.cfg.ai;
    if (ai.text) return !ai.ready || !!ai.ready(v);
    const ph = this.photos[slot];
    return !!(ph && ph.own);
  }

  // What a drawing is made from: the photo (its own address, or the sample), the pet, the style
  // and the kind of background. A change to any of them needs a new drawing; nothing else does.
  #aiKey(v = this.S.v, slot = this.cfg.ai.photo) {
    const ai = this.cfg.ai;
    // Painted from words (ai.text): what it is painted from is the settings module's source()
    if (ai.text) return [slot, ai.source(v, this.L, this.O), '', v[ai.style], ai.kind(v, this.L)].join('|');
    const ph = this.photos[slot];
    const petOf = ai.petOf ? ai.petOf(v, slot) : v.pet;
    return [slot, ph && ph.own ? ph.src : 'sample', petOf || '', v[ai.style], ai.kind(v, this.L)].join('|');
  }

  // The drawing for what the board is set to now, if there is one. An outdoor scene not drawn
  // yet keeps the cut-out drawing of the same photo and style, in front of the drawn scene,
  // rather than falling back to the photo (Duncan 16 Oct 2026); Illustrate again paints one in
  #aiDraw0(slot) {
    if (!this.cfg.ai || !this.ai) return null;
    const key = this.#aiKey(this.S.v, slot || this.cfg.ai.photo);
    const d = this.ai.draws.get(key);
    if (d) return d;
    if (!/\|(head-)?scene$/.test(key)) return null;
    const cut = this.ai.draws.get(key.replace(/scene$/, 'white'));
    return cut && cut.pics.some((p) => p && p.alpha) ? cut : null;
  }

  // The chosen version as the board shows it, { src, alpha }, once it is ready
  #aiPic(slot) {
    if (!this.cfg.ai || !this.ai || !this.cfg.ai.isAi(this.S.v)) return null;
    const d = this.#aiDraw0(slot);
    const p = d && d.pics[d.ver];
    if (!p || !p.src) return null;
    const q = this.#aiInk(p);
    return { src: (q || p).src, alpha: p.alpha, face: p.face };
  }

  // Line work (the settings module's mono: line drawing, linocut) cut out on a dark board: drawn in the
  // board's lettering colour, made once per colour (Duncan 16 Oct 2026: dark ink vanished on dark
  // boards). Null until it is ready, or where it doesn't apply; the board is drawn again then.
  #aiInk(p) {
    const ai = this.cfg.ai;
    if (!p || !p.alpha || !ai.mono || !ai.mono(this.S.v)) return null;
    const c = this.C;
    const hex = String(c.bg || '').replace('#', '');
    const [r, g, b] = [0, 2, 4].map((k) => parseInt(hex.slice(k, k + 2), 16) || 0);
    if (0.2126 * r + 0.7152 * g + 0.0722 * b > 110) return null;
    p.inked = p.inked || {};
    const k = c.ink;
    if (p.inked[k] && p.inked[k].src) return p.inked[k];
    if (!p.inked[k]) {
      p.inked[k] = { loading: true };
      this.#aiMod()
        .then((m) => m.inkPicture(p.src, k))
        .then((q) => {
          p.inked[k] = q;
          this.render();
        })
        .catch(() => (p.inked[k] = { error: true }));
    }
    return null;
  }

  // The customer's sign-in and tries: looked up once, and again before every drawing (the
  // ticket lasts 30 minutes)
  #aiSession(fresh) {
    if (!this.ai.sessionP || fresh) {
      this.ai.sessionP = this.#aiMod()
        .then((m) => m.getSession(this.cfg.ai.session))
        .catch(() => ({ error: true, signedIn: false, triesPerDay: 6 }))
        .then((s) => {
          this.ai.session = s;
          if (s && s.signedIn && !this.ai.mineP) this.ai.mineP = this.#aiMine(s);
          this.#aiDraw();
          return s;
        });
    }
    return this.ai.sessionP;
  }

  // Words for screen readers: the drawing started, finished or failed
  #aiSay(text) {
    const live = this.querySelector('[data-mk-ailive]');
    if (live) live.textContent = text || '';
  }

  // A version made ready for the board: cut out of its white, or as drawn (an outdoor scene)
  // A new painting framed by the settings module (ai.fit: the travel poster trims a paper margin the
  // AI painted round its picture): its position and zoom in the frame, once it is on the board
  async #aiFit(tries) {
    const key = this.ai.fit;
    const fit = this.cfg.ai && this.cfg.ai.fit;
    if (!key) return;
    if (!fit) {
      this.ai.fit = null;
      return;
    }
    // The board may be drawn a frame or two after the values change
    const img = this.board && this.board.querySelector(`img[data-pan="${key}"]`);
    if (!img) {
      if (tries < 30) requestAnimationFrame(() => this.#aiFit(tries + 1));
      else this.ai.fit = null;
      return;
    }
    this.ai.fit = null;
    try {
      await img.decode();
    } catch (e) {
      return;
    }
    const box = img.parentElement.getBoundingClientRect();
    const c = box.width && box.height ? fit(img, box.width, box.height) : null;
    if (!c) return;
    Object.assign(this.S.v, { [key + 'X']: c[0], [key + 'Y']: c[1], [key + 'Zoom']: c[2] });
    this.render();
  }

  async #aiPrepare(d, i) {
    if (d.pics[i] && !d.pics[i].error) return d.pics[i];
    d.pics[i] = { loading: true };
    try {
      const m = await this.#aiMod();
      d.pics[i] = await m.preparePicture(d.versions[i], d.cut);
    } catch (e) {
      d.pics[i] = { error: true };
    }
    return d.pics[i];
  }

  async #aiGo(slot = this.cfg.ai.photo) {
    const ai = this.cfg.ai;
    const w = ai.words;
    const S = this.S;
    if (this.ai.working || !ai.isAi(S.v) || !this.#aiSlots().includes(slot)) return;
    const ph = this.photos[slot];
    if (ai.text ? !this.#aiCan(slot) : !ph || !ph.own || !ph.file) return;
    const key = this.#aiKey(S.v, slot);
    const sfx = this.#sfx(slot);
    const v = Object.assign({}, S.v);
    // The server draws in the ratio nearest the frame the portrait sits in
    const pic = this.board.querySelector(`img[data-pan="${slot}"], img[data-pan="ai${sfx}"]`);
    const frame = pic ? pic.parentElement : this.board.querySelector('.btf-mk-portrait');
    const r = frame ? frame.getBoundingClientRect() : null;
    this.ai.msg = '';
    this.ai.working = { key, slot, text: w.working(v, slot) };
    this.#aiSay(w.working(v, slot) + '. ' + w.eta);
    this.render();
    let res = null;
    let ses = null;
    try {
      const m = await this.#aiMod();
      ses = await this.#aiSession(true);
      if (ses.signedIn && ses.triesLeft > 0) {
        const ratio = m.nearestRatio(r && r.height ? r.width / r.height : 1);
        const body = Object.assign(ai.request(v, slot, this.L), ai.text ? { ratio } : { ratio, photo: await m.photoForServer(ph.file), outline: true });
        // A tester may name the model in the page address (?ai_model=flash); the server ignores it for anyone else
        if (m.testerModel()) body.model = m.testerModel();
        res = await m.illustrate(ses, body);
        if (res.status === 401) {
          // The ticket ran out, or the customer signed out in another tab
          ses = await this.#aiSession(true);
          res = ses.signedIn ? await m.illustrate(ses, body) : null;
        }
      }
    } catch (e) {
      res = { status: 0, json: {} };
    }
    const j = (res && res.json) || {};
    let done = null;
    if (!res) {
      this.ai.msg = !ses || ses.error ? w.failed : ses.setup ? w.unavailable : ses.signedIn && !(ses.triesLeft > 0) ? w.noTries : '';
    } else if (res.status === 200 && Array.isArray(j.versions) && j.versions.length) {
      done = { id: j.id, versions: j.versions.slice(0, 3), model: j.model || '', ver: 0, pics: [], cut: ai.cut(v, this.L) };
      if (ses && Number.isFinite(j.triesLeft)) ses.triesLeft = j.triesLeft;
    } else if (res.status === 429) {
      if (j.busy) this.ai.msg = w.busy;
      else {
        this.ai.msg = w.noTries;
        if (ses) ses.triesLeft = 0;
      }
    } else if (res.status === 502) this.ai.msg = w.retry;
    else if (res.status === 503) {
      this.ai.msg = w.unavailable;
      if (ses) ses.setup = true;
    } else this.ai.msg = w.failed;
    if (done) {
      // A new drawing opens centred in its frame, at its own size
      const first = await this.#aiPrepare(done, 0);
      if (first.error) {
        this.ai.msg = w.failed;
        done = null;
      } else {
        this.ai.draws.set(key, done);
        Object.assign(S.v, { ['ai' + sfx + 'X']: 50, ['ai' + sfx + 'Y']: 50, ['ai' + sfx + 'Zoom']: 1 });
        this.ai.fit = 'ai' + sfx;
        // Kept in Your portraits, so Illustrate again never loses the one before
        const req = ai.request(v, slot, this.L);
        const at = new Date().toISOString();
        this.ai.history.unshift(...done.versions.map((url, i) => ({ id: done.id, n: i + 1, url, file: ph ? ph.file : null, pet: req.pet, style: req.style, bg: req.bg, framing: req.framing, subject: ai.text && ai.subject ? ai.subject(v) : '', model: done.model, at, done })));
        if (new URLSearchParams(location.search).get('ai_model') && done.model && w.drawnWith) this.ai.msg = w.drawnWith(done.model);
      }
    }
    this.ai.working = null;
    this.#aiSay(done ? w.ready(done.versions.length) : this.ai.msg);
    this.render();
    if (done) {
      for (let i = 1; i < done.versions.length; i++) this.#aiPrepare(done, i).then(() => this.#aiDraw());
    }
  }

  async #aiPick(i, slot = this.cfg.ai.photo) {
    const d = this.#aiDraw0(slot);
    if (!d || i < 0 || i >= d.versions.length) return;
    d.ver = i;
    this.render();
    const p = await this.#aiPrepare(d, i);
    if (p.error) this.ai.msg = this.cfg.ai.words.failed;
    this.#aiSay(this.cfg.ai.words.version(i + 1));
    this.render();
  }

  // Your portraits: the customer's drawings from earlier visits, added once they are signed in
  async #aiMine(ses) {
    try {
      const m = await this.#aiMod();
      const list = await m.mine(ses, this.cfg.ai.mine);
      const have = new Set(this.ai.history.map((e) => e.id + ':' + e.n));
      list.forEach((e) => !have.has(e.id + ':' + e.n) && this.ai.history.push(e));
      this.ai.history.sort((a, b) => String(b.at).localeCompare(String(a.at)) || a.n - b.n);
      this.#aiDraw();
    } catch (e) {
      /* the page works without them */
    }
  }

  // The strip of the customer's portraits, once under the Illustrate box. With two portraits a
  // switch says which pet a chosen one goes back on. One drawn for the other kind of design (head
  // only, or head and shoulders) is shown but can't be chosen here. Each can be deleted (asked
  // first, in place), or all of them at once.
  #aiYours(slots) {
    const w = this.cfg.ai.words;
    const all = this.ai.history.slice(0, 24);
    if (!all.length || !w.yours) return '';
    // One row to start with, so the preview above keeps its size; the rest on request
    const list = this.ai.showAll ? all : all.slice(0, 4);
    const v = this.S.v;
    const target = slots.includes(this.ai.target) ? this.ai.target : slots[0];
    const d = this.#aiDraw0(target);
    const head = !!(this.L && this.L.head);
    const lid = 'mk-ai-yours';
    const cur = d ? d.versions[d.ver] : '';
    const ask = this.ai.confirm;
    const switcher =
      slots.length > 1 && w.target
        ? `<div class="btf-mk-ai__target"><span class="btf-mk-lbl">${esc(w.target)}</span>${slots
            .map((sl, si) => `<button type="button" class="btf-mk-tab${sl === target ? ' is-on' : ''}" data-mk-aitarget="${esc(sl)}" aria-pressed="${sl === target}">${esc(w.slotLabel ? w.slotLabel(v, sl, si) : sl)}</button>`)
            .join('')}</div>`
        : '';
    // Best friends draws both pets in one style: once the other pet has its portrait, only saved
    // portraits in that style can join it (another style took the first pet's off, Duncan 16 Oct 2026)
    const other = slots.length > 1 ? slots.find((sl) => sl !== target) : null;
    const pinned = other && this.#aiDraw0(other) ? v[this.cfg.ai.style] : null;
    const cards = list
      .map((e, i) => {
        const clash = !!pinned && !!e.style && e.style !== pinned;
        const fits = (e.framing === 'head') === head && !clash;
        const on = !!cur && cur === e.url;
        const name = (w.histName && w.histName(e)) || e.style || '';
        const title = fits ? name : clash ? (w.histClash ? w.histClash(e) : name) : w.histOther ? w.histOther(e) : '';
        const pick = `<button type="button" class="btf-mk-ai__ver${on ? ' is-on' : ''}" data-mk-aihist="${i}" data-mk-aislot="${esc(target)}" aria-pressed="${on}"${fits ? '' : ' disabled'} title="${esc(title)}"><span class="btf-mk-ai__plate${e.bg === 'scene' && e.framing !== 'head' ? ' is-scene' : ''}"><img src="${esc(e.url)}" alt="" loading="lazy"></span>${ask === i ? '' : `<small>${esc(name)}</small>`}</button>`;
        const del = w.del && ask !== i ? `<button type="button" class="btf-mk-ai__del" data-mk-aidel="${i}" aria-label="${esc(w.del + ' ' + name)}" title="${esc(w.del)}">×</button>` : '';
        const confirm = ask === i ? `<span class="btf-mk-ai__ask"><span>${esc(w.delAsk)}</span><button type="button" class="btf-mk-link" data-mk-aidelyes="${i}">${esc(w.delYes)}</button><button type="button" class="btf-mk-link" data-mk-aidelno>${esc(w.delNo)}</button></span>` : '';
        return `<div class="btf-mk-ai__keep">${pick}${del}${confirm}</div>`;
      })
      .join('');
    const more = all.length > 4 && w.showAll ? `<p class="btf-mk-ai__more"><button type="button" class="btf-mk-link" data-mk-aimore>${esc(this.ai.showAll ? w.showFewer : w.showAll(all.length))}</button></p>` : '';
    const delAll = !w.delAll
      ? ''
      : ask === 'all'
        ? `<p class="btf-mk-ai__ask btf-mk-ai__ask--all"><span>${esc(w.delAllAsk)}</span><button type="button" class="btf-mk-link" data-mk-aidelyes="all">${esc(w.delYes)}</button><button type="button" class="btf-mk-link" data-mk-aidelno>${esc(w.delNo)}</button></p>`
        : `<p class="btf-mk-ai__delall"><button type="button" class="btf-mk-link" data-mk-aidelall>${esc(w.delAll)}</button></p>`;
    return `<div class="btf-mk-ai__vers btf-mk-ai__yours"><span class="btf-mk-field__row"><span class="btf-mk-lbl" id="${lid}">${esc(w.yours)}</span></span>${w.yoursNote ? `<p class="btf-mk-ai__fine">${esc(w.yoursNote)}</p>` : ''}${switcher}<div class="btf-mk-ai__grid" role="group" aria-labelledby="${lid}">${cards}</div>${more}${delAll}</div>`;
  }

  // Illustrate both: each portrait not drawn yet (or both again), one after the other
  async #aiBoth() {
    const slots = this.#aiSlots();
    const todo = slots.filter((sl) => !this.#aiDraw0(sl));
    for (const sl of todo.length ? todo : slots) {
      await this.#aiGo(sl);
      if (!this.#aiDraw0(sl)) break;
    }
  }

  // Deleting saved portraits: one (its index in the history) or 'all'. A deleted picture comes off
  // the board too, since it can no longer be printed; one on an order already is only hidden.
  async #aiDelete(which) {
    const w = this.cfg.ai.words;
    const list = which === 'all' ? this.ai.history.slice() : [this.ai.history[which]].filter(Boolean);
    this.ai.confirm = null;
    if (!list.length) return this.#aiDraw();
    const m = await this.#aiMod();
    const ses = await this.#aiSession(true);
    const r = await m.forget(ses, which === 'all' ? { all: true } : { id: list[0].id, n: list[0].n });
    if (!r.ok) {
      this.ai.msg = w.delFailed;
      return this.#aiDraw();
    }
    const gone = new Set(list.map((e) => e.url));
    const kept = new Set(r.keptUrls || []);
    this.ai.history = this.ai.history.filter((e) => !gone.has(e.url));
    for (const [k, d] of [...this.ai.draws]) if (d.versions.some((u) => gone.has(u) && !kept.has(u))) this.ai.draws.delete(k);
    this.ai.msg = r.kept ? w.deletedKept : which === 'all' ? w.deletedAll : w.deleted;
    this.#aiSay(this.ai.msg);
    this.#drawFields();
    this.render();
  }

  // A portrait from Your portraits back on the board, with no try spent: its style, pet and kind of
  // background, and the photo it was drawn from (needed to order the board)
  async #aiUse(i, slot = this.cfg.ai.photo) {
    const ai = this.cfg.ai;
    const w = ai.words;
    const e = this.ai.history[i];
    if (!e || this.ai.working || (e.framing === 'head') !== !!(this.L && this.L.head)) return;
    const S = this.S;
    const v = S.v;
    // A different pet goes through the pet choice, so its starting words and samples follow
    const petKey = slot === 'photo2' ? 'pet2' : 'pet';
    if (e.pet && v[petKey] !== e.pet && this.cfg.fields.some((f) => f.key === petKey)) this.#choose(petKey, e.pet);
    if (e.style) {
      // Where each style is a design (the travel poster), the poster comes back on its own
      const li = this.cfg.layouts.findIndex((l) => l.id === e.style);
      if (li >= 0 && li !== S.layout) {
        this.touched.s1 = true;
        this.#switch(li);
      }
      v[ai.style] = e.style;
      this.typed.add(ai.style);
    }
    if (e.bg === 'scene') v.bg = 'scene';
    else if (v.bg === 'scene') v.bg = e.bg || 'plain';
    this.#aiSay(w.restoring);
    this.ai.msg = w.restoring;
    this.render();
    // Painted from words: the place it was painted from goes back in the fields, no photo to fetch
    if (ai.text) {
      if (ai.restore) {
        // What comes back is the customer's own, so a design's showcase words don't replace it
        const was = Object.assign({}, v);
        ai.restore(e, v);
        Object.keys(v).forEach((k) => {
          if (v[k] === was[k]) return;
          this.typed.add(k);
          if (this.cfg.changed) this.cfg.changed(k, this);
        });
      }
      const done = e.done || { id: e.id, versions: [e.url], model: e.model || '', ver: 0, pics: [], cut: ai.cut(v, this.L) };
      e.done = done;
      const at = Math.max(0, done.versions.indexOf(e.url));
      const p = await this.#aiPrepare(done, at);
      if (p.error) this.ai.msg = w.lost;
      else {
        done.ver = at;
        this.ai.draws.set(this.#aiKey(v, slot), done);
        const sfx = this.#sfx(slot);
        Object.assign(v, { ['ai' + sfx + 'X']: 50, ['ai' + sfx + 'Y']: 50, ['ai' + sfx + 'Zoom']: 1 });
        this.ai.fit = 'ai' + sfx;
        this.ai.msg = '';
      }
      this.#aiSay(p.error ? w.lost : w.backOn || w.ready(1));
      this.#drawFields();
      this.render();
      return;
    }
    let file = e.file || null;
    if (!file && e.photo) {
      try {
        const r = await fetch(e.photo);
        if (r.ok) {
          const b = await r.blob();
          file = new File([b], 'your-photo.' + ((b.type || 'image/jpeg').split('/')[1] || 'jpg'), { type: b.type || 'image/jpeg' });
        }
      } catch (x) {
        file = null;
      }
    }
    const ph = this.photos[slot];
    if (file && !(ph && ph.own && ph.file === file)) {
      if (await this.#photoLoad(slot, file)) e.file = file;
      else file = null;
    }
    const own = this.photos[slot] && this.photos[slot].own;
    const done = e.done || { id: e.id, versions: [e.url], model: e.model || '', ver: 0, pics: [], cut: ai.cut(v, this.L) };
    e.done = done;
    const at = Math.max(0, done.versions.indexOf(e.url));
    const p = own ? await this.#aiPrepare(done, at) : { error: true };
    if (p.error) this.ai.msg = w.lost;
    else {
      done.ver = at;
      this.ai.draws.set(this.#aiKey(v, slot), done);
      // Two portraits: the next one chosen goes on the pet still without one
      const next = this.#aiSlots().find((sl) => sl !== slot && !this.#aiDraw0(sl));
      if (next) this.ai.target = next;
      const sfx = this.#sfx(slot);
      Object.assign(v, { ['ai' + sfx + 'X']: 50, ['ai' + sfx + 'Y']: 50, ['ai' + sfx + 'Zoom']: 1 });
      this.ai.fit = 'ai' + sfx;
      this.ai.msg = '';
    }
    this.#aiSay(p.error ? w.lost : w.backOn || w.ready(1));
    this.#drawFields();
    this.render();
  }

  // A basket line being edited: its drawings come back from the AI server's pictures, with no try
  // spent (the customer's photos first, so each drawing is filed under its photo). The first
  // portrait's properties are _ai_*, a second's _ai2_*.
  // A shared link's drawing: put on the board as drawn from our sample photo, so it stays until
  // the person opening it adds their own photo or changes the style
  async #aiFromShared() {
    const ai = this.cfg.ai;
    if (!ai || !ai.isAi(this.S.v)) return;
    for (const [slot, url] of Object.entries(this.sharedAi || {})) {
      if (!this.#aiSlots().includes(slot)) continue;
      const d = { id: url.split('/')[5], versions: [url], model: '', ver: 0, pics: [], cut: ai.cut(this.S.v, this.L) };
      const key = this.#aiKey(this.S.v, slot);
      const p = await this.#aiPrepare(d, 0);
      if (!p.error) this.ai.draws.set(key, d);
      this.render();
    }
  }

  async #aiFromLine(props) {
    const ai = this.cfg.ai;
    if (!ai || !ai.isAi(this.S.v)) return;
    for (const slot of this.#aiSlots()) {
      const sfx = this.#sfx(slot);
      const P = (k) => props['_ai' + sfx + '_' + k];
      if (!P('id')) continue;
      const versions = String(P('versions') || P('url') || '').split(/\s+/).filter((u) => /^https:\/\//.test(u));
      if (!versions.length) continue;
      const ver = Math.min(versions.length, Math.max(1, Number(P('version')) || 1)) - 1;
      const d = { id: P('id'), versions, model: P('model') || '', ver, pics: [], cut: ai.cut(this.S.v, this.L) };
      const [x, y, z] = String(P('crop') || '').split(',').map(Number);
      if ([x, y, z].every(Number.isFinite)) Object.assign(this.S.v, { ['ai' + sfx + 'X']: x, ['ai' + sfx + 'Y']: y, ['ai' + sfx + 'Zoom']: z });
      const key = this.#aiKey(this.S.v, slot);
      this.#aiSay(ai.words.restoring);
      const p = await this.#aiPrepare(d, ver);
      if (p.error) this.ai.msg = ai.words.lost;
      else this.ai.draws.set(key, d);
      this.#aiSay(p.error ? ai.words.lost : ai.words.version(ver + 1));
      this.render();
      versions.forEach((u, i) => i !== ver && this.#aiPrepare(d, i).then(() => this.#aiDraw()));
    }
  }

  // The Illustrate box under the preview (one part per portrait the design draws), the stage's
  // badge, and the working veil on the preview
  #aiDraw() {
    const ai = this.cfg.ai;
    const box = this.querySelector('[data-mk-ai]');
    if (!ai || !box) return;
    const w = ai.words;
    const v = this.S.v;
    const ses = this.ai.session;
    const all = (ses && ses.triesPerDay) || 6;
    const left = ses && ses.signedIn ? Math.max(0, Number(ses.triesLeft) || 0) : null;
    const isAi = ai.isAi(v);
    // An illustrated style on the board (chosen, shared or brought back from the basket): look up
    // the customer's sign-in and tries
    if (isAi && !this.ai.sessionP) this.#aiSession();
    const slots = this.#aiSlots();
    const many = slots.length > 1;
    const working = this.ai.working;
    const tries = left == null ? '' : `<span>${esc(w.tries(left, all))}</span>`;
    let html = '';
    if (!isAi) html = `<p class="btf-mk-ai__fine">${esc(typeof w.rawNote === 'function' ? w.rawNote(v, this.L) : w.rawNote)}</p>`;
    else {
      // Signed out, or the server not set up: one note for the whole box
      const out = ses && !ses.signedIn && !ses.error && !ses.setup;
      let fineAll = '';
      const parts = [];
      slots.forEach((slot, si) => {
        const own = this.#aiCan(slot);
        const d = this.#aiDraw0(slot);
        const stale = !d && this.ai.draws.size > 0 && [...this.ai.draws.keys()].some((k) => k.startsWith(slot + '|'));
        const btn = (label, on) => `<button type="button" class="btf-mk-btn" data-mk-aigo="${esc(slot)}"${on ? '' : ' disabled'}>${esc(label)}</button>`;
        // Two portraits: a card for each pet, its photo, name and whether it is drawn yet
        let part = '';
        if (many) {
          const ph = this.photos[slot];
          const state = !own ? w.statusPhoto : d ? w.statusDone : w.statusTodo;
          part = `<div class="btf-mk-ai__head">${ph && ph.src ? `<img src="${esc(ph.src)}" alt="">` : ''}<span><b>${esc(w.slotLabel ? w.slotLabel(v, slot, si) : slot)}</b>${state ? `<small class="${d ? 'is-done' : ''}">${esc(state)}</small>` : ''}</span></div>`;
        }
        // Only offered when the server drew more than one (it draws one a try from 15 Oct 2026)
        if (d && d.versions.length > 1) {
          const lid = 'mk-ai-vers' + this.#sfx(slot);
          part += `<div class="btf-mk-ai__vers"><span class="btf-mk-field__row"><span class="btf-mk-lbl" id="${lid}">${esc(w.chooseVersion)}</span></span><div class="btf-mk-ai__grid" role="group" aria-labelledby="${lid}">${d.versions
            .map((u, i) => {
              const p = d.pics[i];
              const on = d.ver === i;
              return `<button type="button" class="btf-mk-ai__ver${on ? ' is-on' : ''}" data-mk-aiver="${i}" data-mk-aislot="${esc(slot)}" aria-pressed="${on}"><span class="btf-mk-ai__plate${d.cut ? '' : ' is-scene'}" style="background: ${this.C.bg};">${p && p.src ? `<img src="${esc(p.src)}" alt="">` : ''}</span><small>${esc(w.version(i + 1))}</small></button>`;
            })
            .join('')}</div></div>`;
        }
        let go = '';
        let fine = '';
        const verb = d ? w.again : w.verb(v, slot);
        if (working) go = btn(working.slot === slot ? w.workBadge : verb, false) + (many ? '' : tries);
        else if (ses && ses.setup) {
          go = btn(w.verb(v, slot), false);
          if (si === 0) fineAll = w.unavailable;
        } else if (out) {
          // Shopify's customer login (an emailed code), coming back to this page as it is. The
          // theme's own account link (routes.account_login_url) cannot carry a return address
          if (si === 0) {
            // The design comes back with them (#resumeSave keeps it in this browser meanwhile)
            const q = new URLSearchParams(window.location.search);
            q.set('btf_resume', '1');
            const back = window.location.pathname + '?' + q.toString();
            go = `<a class="btf-mk-btn" data-mk-signin href="/customer_authentication/login?return_to=${encodeURIComponent(back)}">${esc(w.signInLink)}</a><span>${esc(w.triesOut(all))}</span>`;
            fineAll = w.signIn;
          }
          if (!own) fine = w.needPhoto(v, slot);
        } else if (!own) {
          go = btn(w.verb(v, slot), false) + (many ? '' : tries);
          fine = w.needPhoto(v, slot);
        } else if (!ses) go = btn(verb, false) + `<span>${esc(w.checking)}</span>`;
        else {
          go = btn(verb, ses.error || left > 0) + (many ? '' : tries);
          fine = (stale ? w.staleNote + ' ' : '') + (many ? '' : d ? w.after : w.before);
        }
        if (go) part += `<div class="btf-mk-ai__go">${go}</div>`;
        if (fine) part += `<p class="btf-mk-ai__fine">${esc(fine)}</p>`;
        parts.push(many ? `<div class="btf-mk-ai__slot">${part}</div>` : part);
      });
      html += many ? `${w.bothNote ? `<p class="btf-mk-ai__fine btf-mk-ai__both-note">${esc(w.bothNote(v))}</p>` : ''}<div class="btf-mk-ai__pair">${parts.join('')}</div>` : parts.join('');
      // Two portraits: Illustrate both (once both photos are in), then the tries and the note once
      if (many && ses && ses.signedIn && !ses.setup) {
        const allOwn = slots.every((s) => this.photos[s] && this.photos[s].own);
        const todo = slots.filter((s) => !this.#aiDraw0(s)).length || slots.length;
        const both = allOwn && w.both ? `<button type="button" class="btf-mk-btn" data-mk-aiboth${!working && (left == null || left >= todo || ses.error) ? '' : ' disabled'}>${esc(w.both)}</button>${todo > 1 && w.bothTries ? `<span>${esc(w.bothTries)}</span>` : ''}` : '';
        html += `<div class="btf-mk-ai__go">${both}${tries}</div><p class="btf-mk-ai__fine">${esc(slots.some((s) => this.#aiDraw0(s)) ? w.after : w.before)}</p>`;
      }
      if (this.ai.msg) html += `<p class="btf-mk-ai__msg">${esc(this.ai.msg)}</p>`;
      if (fineAll) html += `<p class="btf-mk-ai__fine">${esc(fineAll)}</p>`;
      // Your portraits, once, under everything
      html += this.#aiYours(slots);
    }
    box.hidden = false;
    if (box.dataset.html !== html) {
      const a = document.activeElement;
      const focus = a && box.contains(a) ? (a.matches('[data-mk-aiver]') ? `[data-mk-aiver="${a.dataset.mkAiver}"][data-mk-aislot="${a.dataset.mkAislot}"]` : a.matches('[data-mk-aigo]') ? `[data-mk-aigo="${a.dataset.mkAigo}"]` : '') : '';
      box.innerHTML = html;
      box.dataset.html = html;
      const back = focus && box.querySelector(focus);
      if (back) back.focus({ preventScroll: true });
      else if (focus) box.querySelector('[data-mk-aiver], .btf-mk-btn')?.focus({ preventScroll: true });
    }
    // The badge over the preview says what the portrait is
    const badge = this.querySelector('.btf-mk-stage__badge');
    if (badge) {
      if (!badge.dataset.text) badge.dataset.text = badge.textContent;
      const ds = slots.map((s) => this.#aiDraw0(s));
      const d = ds[0];
      const allDone = ds.every((x) => x) && slots.every((s) => this.#aiPic(s));
      // The settings module may name what is on the board instead (our example drawing)
      const own = !working && !(isAi && allDone) && w.badge ? w.badge(v, this.L) : '';
      badge.textContent = own || (!isAi ? badge.dataset.text : working ? w.workBadge : allDone ? (many || d.versions.length < 2 ? w.doneBadge : `${w.doneBadge} · ${w.version(d.ver + 1).toLowerCase()} of ${d.versions.length}`) : w.exampleBadge);
      badge.classList.toggle('is-on', isAi && allDone);
    }
    // The working veil, over the preview while the server draws
    const stage = this.querySelector('[data-mk-stage]');
    let veil = stage && stage.querySelector('[data-mk-work]');
    if (stage && working && !veil) {
      veil = document.createElement('div');
      veil.className = 'btf-mk-work';
      veil.dataset.mkWork = '';
      veil.setAttribute('aria-hidden', 'true');
      veil.innerHTML = `<b>${esc(working.text)}</b><span>${esc(w.eta)}</span><i></i>`;
      stage.append(veil);
    } else if (veil && !working) veil.remove();
    // The zoom follows the picture in the frame: the photo, or the drawing
    this.cfg.fields.filter((f) => f.type === 'photozoom').forEach((pz) => {
      if (this.#panKey(pz.photo) === this.ai.lastPan[pz.key]) return;
      const el = this.querySelector(`[data-mk-f="${pz.key}"]`);
      if (el) el.outerHTML = this.#zoomHtml(pz);
    });
  }

  /* --------------------------------------------------------- share picture */

  #design() {
    const cm = this.cm();
    return {
      art: this.board.querySelector('.btf-mk-board__art'),
      board: { w: cm[0] * 10, h: cm[1] * 10 },
      fonts: this.setup.fonts,
      libs: { pdfLib: this.setup.assets.pdfLib, fontkit: this.setup.assets.fontkit },
      capUrl: this.caps === 'black' ? this.setup.assets.capBlack : this.setup.assets.capWhite,
      // The customer's photos, by the address the board shows them at: the print is made from
      // the original file, not the screen copy
      photos: Object.fromEntries(Object.values(this.photos).filter((p) => p.own).map((p) => [p.src, { file: p.file, w: p.w, h: p.h }])),
    };
  }

  async #settled() {
    // Every sky or map on the preview drawn, every face loaded, then two frames for the layout
    if (document.fonts) await document.fonts.ready;
    await Promise.resolve(this.drawing);
    // Every photo on the preview decoded, so it is measured at its own size
    await Promise.all([...this.board.querySelectorAll('img')].map((i) => (i.decode ? i.decode().catch(() => {}) : null)));
    // A map with a tile that would not load is drawn again before it is printed
    if (this.board.querySelector('svg[data-incomplete]')) {
      this.render();
      await Promise.resolve(this.drawing);
      if (this.board.querySelector('svg[data-incomplete]')) throw new Error('part of the map did not load');
    }
    await new Promise((r) => setTimeout(r, 60));
    // Two frames for the layout to settle; a tab in the background gets no frames, so a short
    // timer stands in rather than leaving Add to basket waiting
    await Promise.race([
      new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r))),
      new Promise((r) => setTimeout(r, 250)),
    ]);
  }

  async #picture() {
    await this.#settled();
    const { buildPicture } = await import(this.setup.assets.artwork);
    return buildPicture(this.#design());
  }

  /* --------------------------------------------------------- add to basket */

  #ask(text) {
    const msg = this.querySelector('[data-mk-msg]');
    if (!msg) return;
    msg.textContent = text;
    msg.hidden = !text;
  }

  // A copy of the size buttons under the message: the click is handled by the same delegated
  // listener as the originals, which clears the message and the copy with it
  #sizesHere(msgSel, sizesSel) {
    const msg = this.querySelector(msgSel);
    const sizes = this.querySelector(sizesSel);
    if (!msg || !sizes) return;
    const copy = sizes.cloneNode(true);
    [...copy.attributes].filter((a) => a.name.startsWith('data-')).forEach((a) => copy.removeAttribute(a.name));
    copy.classList.add('btf-mk-sizes--here');
    msg.append(copy);
  }

  #clearAsk() {
    this.#ask('');
  }

  #properties() {
    const S = this.S;
    const out = {
      Design: this.lname(),
      Format: this.O === 'land' ? 'Landscape' : 'Portrait',
      Colour: this.C.name,
    };
    this.cfg.fields.forEach((f) => {
      if (!this.visible(f) || f.type === 'place' || f.type === 'photozoom') return;
      if (f.type === 'photo') {
        // The photo itself goes on the line as a file (#submit); here, its name and its framing
        const ph = this.photos[f.key] || {};
        out[f.prop || 'Photo'] = ph.own ? ph.name || 'Your photo' : 'No photo uploaded';
        // A second photo (Best friends) numbers its own: _Photo 2 size, _Photo 2 crop
        const n = f.tag || '';
        if (ph.own && ph.w) out['_Photo' + n + ' size'] = `${ph.w} × ${ph.h} px`;
        out['_Photo' + n + ' crop'] = `${Number(S.v[f.key + 'X']).toFixed(1)}% across, ${Number(S.v[f.key + 'Y']).toFixed(1)}% down, zoom ${Number(S.v[f.key + 'Zoom']).toFixed(2)}× (object-fit cover, object-position and scale about that point)`;
        const dpi = this.cfg.ai && this.#aiAll().includes(f.key) && this.cfg.ai.isAi(S.v) ? 0 : this.photoDpi(f.key);
        if (dpi && dpi < 150) out['Print check' + n] = `Photo about ${dpi} dpi at ${this.S.size} (150 recommended)`;
        return;
      }
      const v = S.v[f.key];
      if (v == null || v === '') return;
      // A slider left where it started says nothing on the order
      if (f.type === 'range' && Number(v) === Number(f.value)) return;
      let label = f.prop || this.label(f);
      // Two fields can share a label (Born, Born, Born): number them so none is lost
      if (out[label] != null) {
        let k = 2;
        while (out[label + ' ' + k] != null) k++;
        label = label + ' ' + k;
      }
      const opt = f.type === 'choice' && this.#option(f, v);
      out[label] = f.type === 'range' ? v + (f.unit || '') : f.type === 'date' ? H.date(v) : f.type === 'toggle' ? (v ? 'On' : 'Off') : opt ? opt[1] : String(v);
    });
    Object.assign(out, this.cfg.properties ? this.cfg.properties(S.v, this.L) : {});
    Object.assign(out, this.cfg.orderProperties ? this.cfg.orderProperties(this) : {});
    // The drawn portrait: which drawing and version, and where it sits in its frame. The studio
    // finds the request (photo, style, prompt) beside the pictures on the AI server by its id
    // A second pet's drawing (Best friends) is _ai2_*
    if (this.cfg.ai && this.cfg.ai.isAi(S.v)) {
      this.#aiSlots().forEach((slot) => {
        const d = this.#aiDraw0(slot);
        if (!d) return;
        const s = this.#sfx(slot);
        const p = '_ai' + s + '_';
        out[p + 'id'] = d.id;
        out[p + 'version'] = String(d.ver + 1);
        out[p + 'model'] = d.model || '';
        out[p + 'url'] = d.versions[d.ver];
        out[p + 'versions'] = d.versions.join(' ');
        out[p + 'crop'] = [S.v['ai' + s + 'X'], S.v['ai' + s + 'Y'], S.v['ai' + s + 'Zoom']].map((n) => +Number(n).toFixed(3)).join(',');
        out[p + 'cutout'] = d.pics[d.ver] && d.pics[d.ver].alpha ? 'yes, the white cut away in the browser' : 'no, used as drawn';
      });
    }
    out['Screw caps'] = this.caps === 'black' ? 'Black' : 'White';
    if (S.tsz !== 100) out['Text size'] = S.tsz + '%';
    if (S.tal) out.Alignment = S.tal.charAt(0).toUpperCase() + S.tal.slice(1);
    out._maker = this.cfg.key;
    out._layout = this.L.id;
    out._colours = [this.C.bg, this.C.ink, this.C.acc].join(' ');
    out._design = this.#toParams().toString();
    out._edit_url = window.location.pathname;
    // Not _layout_version: that marks a Quotes and sayings line for its approval email block
    out._maker_version = '1';
    return out;
  }

  async #submit(e) {
    const form = e.target;
    if (form !== this.form) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.#busy) return;
    // A board with our sample photo on it cannot be ordered: the samples are not ours to print
    const noPhoto = this.cfg.fields.find((f) => f.type === 'photo' && this.visible(f) && !(this.photos[f.key] && this.photos[f.key].own));
    if (noPhoto) {
      // The upload comes to the button, as the sizes do, so the page stays where the customer is
      const w = noPhoto.words || {};
      this.#ask(w.needed || 'Upload your photo first. The board shows our sample until you do.');
      const msg = this.querySelector('[data-mk-msg]');
      if (msg) {
        const up = document.createElement('label');
        up.className = 'btf-mk-photo__up is-red btf-mk-photo__up--here';
        up.innerHTML = `<input type="file" accept="${esc(noPhoto.accept || 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif')}" class="visually-hidden" data-mk-photo="${esc(noPhoto.key)}"><span>${esc(w.upload || 'Upload your photo')}</span>`;
        msg.append(up);
        up.querySelector('input').focus({ preventScroll: true });
      }
      return;
    }
    // Anything else the settings module says must change first (a head-alone pet design needs an
    // illustrated style)
    const blocked = this.cfg.block ? this.cfg.block(this.S.v, this.L) : '';
    if (blocked) {
      this.#ask(blocked);
      this.#goTo('mk-step-4', false);
      return;
    }
    // An illustrated style needs its portrait drawn and a version chosen first
    if (this.cfg.ai && this.cfg.ai.isAi(this.S.v) && this.#aiSlots().some((slot) => !this.#aiPic(slot))) {
      this.#ask(this.cfg.ai.words.needFirst(this.S.v, this.#aiSlots()));
      this.#goTo('mk-ai', false);
      return;
    }
    if (!this.S.size) {
      // The sizes come to the button, so the page stays where the customer is
      this.#ask(this.form.dataset.sizeFirst || 'Choose a size first.');
      this.#sizesHere('[data-mk-msg]', '[data-mk-sizes]');
      return;
    }
    // Every field the design shows must be filled
    const missing = this.cfg.fields.find((f) => this.visible(f) && !f.optional && f.type !== 'photozoom' && (this.S.v[f.key] == null || this.S.v[f.key] === ''));
    if (missing) {
      this.#ask('Fill in ' + this.label(missing).toLowerCase() + ' first.');
      this.#goTo('mk-step-4');
      return;
    }
    this.#clearAsk();
    // The customer's go-ahead, ticked beside the button
    const PJ = await printJpegModule();
    const approved = PJ.approval(this);
    if (approved === null) return;
    this.#busy = true;
    const button = form.querySelector('[type="submit"]');
    const label = button ? button.innerHTML : '';
    const L = this.setup.labels || {};
    const say = (t) => button && (button.textContent = t);
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    say(L.preparing || 'Preparing your artwork…');

    const v = this.#variant();
    const cm = this.cm();
    const stamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-');
    const base = `${this.cfg.key}-${this.L.id}-${this.S.size.toLowerCase()}-${this.O === 'land' ? 'landscape' : 'portrait'}-${stamp}`;
    const props = this.#properties();

    let files = null;
    let pending = '';
    try {
      await this.#settled();
      const { buildArtwork } = await import(this.setup.assets.artwork);
      files = await buildArtwork(Object.assign(this.#design(), {
        meta: {
          title: `${this.cfg.name} · ${this.lname()} · ${this.C.name} · ${this.S.size} ${props.Format} ${cm[0] * 10} × ${cm[1] * 10} mm`,
          subject: props._design,
          keywords: [this.cfg.name, this.lname(), this.C.name, this.S.size, props.Format],
          creator: 'Beyond the Fridge · ' + this.cfg.name + ' · layout 1',
        },
      }));
    } catch (err) {
      pending = `yes — the print file was not made in the browser (${err && err.message ? err.message : 'unknown error'}); make the artwork from the properties`;
    }

    let post;
    try {
      post = await import(this.setup.assets.cartPost);
    } catch (err) {
      this.#busy = false;
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = label;
      }
      this.#ask('Something went wrong adding your board. Please try again.');
      return;
    }
    // The customer's own photos, as they chose them: the studio prints from the original
    const originals = [];
    for (const f of this.cfg.fields.filter((x) => x.type === 'photo' && this.visible(x))) {
      const ph = this.photos[f.key];
      if (!ph || !ph.own || !ph.file) continue;
      let file = ph.file;
      let note = '';
      if (file.size > PHOTO_MAX) {
        // Too big for a file property: a full-size JPEG copy instead, and a note for the studio
        try {
          file = await fullCopy(file, PHOTO_MAX);
          note = `the original was ${(ph.file.size / 1048576).toFixed(1)} MB, over the 20 MB limit for a basket file, so this is a full-size JPEG copy; ask the customer for the original if it is needed`;
        } catch (err) {
          file = null;
          note = `yes — the original is ${(ph.file.size / 1048576).toFixed(1)} MB, over the 20 MB limit for a basket file; ask the customer for the photo`;
        }
      }
      originals.push({ f, file, note, name: ph.name || 'photo.jpg' });
    }
    // The artwork, the original and the approval first, so the order reads top down
    const lineData = (withFiles) => {
      const fd = new FormData();
      fd.set('id', v.id);
      fd.set('quantity', '1');
      if (withFiles && files && files.flat) fd.append(`properties[${PRINT_FILE}]`, files.flat, base + '-print-150dpi.jpg');
      if (approved) fd.append(`properties[${APPROVED}]`, approved);
      Object.entries(props).forEach(([k, val]) => fd.append(`properties[${k}]`, val));
      originals.forEach((o) => o.note && fd.append(`properties[_${o.f.prop || 'Photo'} note]`, o.note));
      return fd;
    };
    const withOriginals = (fd) => {
      originals.forEach((o) => o.file && fd.append(`properties[${o.f.fileProp || PHOTO_PROP}]`, o.file, o.name));
      return fd;
    };
    say(L.adding || 'Adding to your basket…');
    let res = null;
    let reason = '';
    // Two files: the artwork and the original. Only if the artwork could not be drawn does the
    // PDF go instead, with the small picture for the basket.
    for (const once of [true]) {
      try {
        const fd = withOriginals(lineData(once));
        if (files && !files.flat) {
          fd.append('properties[_Artwork PDF (print from this)]', files.pdf, base + '.pdf');
          fd.append('properties[Your artwork]', files.png, base + '-preview.png');
        } else if (!files) fd.append('properties[_artwork_pending]', pending);
        res = await post.postForm('/cart/add.js', fd, (p) => say(`${L.adding || 'Adding to your basket…'} ${Math.round(p * 100)}%`));
        reason = res ? 'status ' + res.status : 'no response';
      } catch (err) {
        reason = err && err.message ? err.message : 'the upload failed';
      }
      if (post.added(res)) break;
    }
    // The customer's photo is the one thing the studio cannot make again: try it on its own
    if (!post.added(res) && files && originals.some((o) => o.file)) {
      const fd = withOriginals(lineData());
      fd.append('properties[_artwork_pending]', `yes — the print files did not attach (${reason}); make the artwork from the properties and the photo`);
      try {
        res = await post.postForm('/cart/add.js', fd, (p) => say(`${L.adding || 'Adding to your basket…'} ${Math.round(p * 100)}%`));
      } catch (err) {
        res = null;
      }
    }
    if (!post.added(res) && (files || originals.length)) {
      const fd = lineData();
      fd.append('properties[_artwork_pending]', `yes — the print files did not attach (${reason}); make the artwork from the properties${originals.length ? ' and ask the customer for their photo' : ''}`);
      try {
        res = await post.postForm('/cart/add.js', fd, null);
      } catch (err) {
        res = null;
      }
    }
    if (post.added(res)) {
      await PJ.tidyLine(res.json);
      const editing = window.btfEditLine;
      if (editing && editing.active) await editing.replaceOriginal();
      await post.announce({ variantId: v.id, productId: '', sourceId: 'btf-maker' });
      window.location.assign('/cart');
      return;
    }
    this.#busy = false;
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = label;
    }
    this.#ask((res && res.json && (res.json.description || res.json.message)) || 'Something went wrong adding your board. Please try again.');
  }

  /* ------------------------------------------------------------ the studio */

  /**
   * The studio print page (sections/btf-studio-print.liquid, assets/btf-studio-print.js) puts a
   * bare maker, marked data-studio, out of sight on the page for each board it rebuilds (a
   * storefront page cannot be framed), and rebuilds an ordered board from its line's properties,
   * the way a basket line being edited is restored. The page asks through the element's
   * `studio` property:
   *
   * - restore(props): the design, the customer's photos from the line's files and the chosen
   *   drawings from the AI server. Resolves a summary, with `problems` for anything missing.
   * - build({ '': url, '2': url }): the print files, with each drawing replaced by its print-size
   *   version (the AI server's /enlarge, fetched by the studio page), cut out of its white again
   *   at that size where the board's was. Resolves { files, pictures }: the files buildArtwork
   *   makes and, for each picture on the board, its pixels and its dpi at the board's size.
   *
   * Nothing here runs for a customer: the maker pages have no data-studio.
   */
  #studioHook() {
    this.studio = {
      restore: (props) => this.#studioRestore(props || {}),
      build: (big) => this.#studioBuild(big || {}),
    };
    this.dispatchEvent(new CustomEvent('btf:studio-ready'));
  }

  async #studioRestore(props) {
    if (props._design) this.#fromParams(new URLSearchParams(props._design), true);
    await this.#photosFromLine(props);
    await this.#aiFromLine(props);
    this.render();
    await this.#settled();
    const S = this.S;
    const problems = [];
    this.cfg.fields.filter((f) => f.type === 'photo' && this.visible(f)).forEach((f) => {
      if (!(this.photos[f.key] && this.photos[f.key].own)) problems.push(`The customer’s ${(f.prop || 'photo').toLowerCase()} could not be loaded from the order.`);
    });
    const isAi = !!(this.cfg.ai && this.cfg.ai.isAi(S.v));
    const drawings = [];
    if (isAi) {
      this.#aiSlots().forEach((slot) => {
        const d = this.#aiDraw0(slot);
        const sfx = this.#sfx(slot);
        const p = d && d.pics[d.ver];
        if (!p || !p.src) problems.push(`The chosen drawing${sfx ? ' ' + sfx : ''} could not be loaded from the AI server.`);
        else drawings.push({ sfx, id: d.id, version: d.ver + 1, w: p.w, h: p.h, cut: !!p.alpha });
      });
    }
    const cm = this.cm();
    return {
      design: this.lname(),
      size: S.size,
      format: this.O === 'land' ? 'Landscape' : 'Portrait',
      colour: this.C.name,
      cm,
      isAi,
      drawings,
      problems,
    };
  }

  async #studioBuild(big) {
    if (!this.S.size) throw new Error('the line has no size');
    const m = this.cfg.ai ? await this.#aiMod() : null;
    await this.#settled();
    const design = this.#design();
    // The pixels of every picture on the board, by the address it is shown at, for the dpi
    const sizes = {};
    Object.values(this.photos).filter((p) => p.own).forEach((p) => (sizes[p.src] = { w: p.w, h: p.h, what: 'photo' }));
    if (this.cfg.ai && this.cfg.ai.isAi(this.S.v)) {
      for (const slot of this.#aiSlots()) {
        const sfx = this.#sfx(slot);
        const d = this.#aiDraw0(slot);
        const p = d && d.pics[d.ver];
        if (!p || !p.src) throw new Error('a drawing is missing');
        // The address the board shows it at: the inked copy on a dark board
        const ink = this.#aiInk(p);
        const shown = (ink || p).src;
        sizes[shown] = { w: p.w, h: p.h, what: 'drawing' };
        if (!big[sfx]) continue;
        const q = await m.preparePicture(big[sfx], d.cut);
        let qi = null;
        try {
          if (p.alpha && !q.alpha) throw new Error('the print-size drawing could not be cut out of its white');
          // The same picture, larger: anything but the same shape would stretch it
          if (Math.abs(q.w / q.h - p.w / p.h) > 0.005) throw new Error(`the print-size drawing is ${q.w} × ${q.h}, not the shape of the chosen one (${p.w} × ${p.h})`);
          if (ink) qi = await m.inkPicture(q.src, this.C.ink);
          const file = await fetch((qi || q).src).then((r) => r.blob());
          design.photos[shown] = { file, w: q.w, h: q.h };
          sizes[shown] = { w: q.w, h: q.h, what: 'drawing', big: true };
        } finally {
          URL.revokeObjectURL(q.src);
          if (qi) URL.revokeObjectURL(qi.src);
        }
      }
    }
    const cm = this.cm();
    const props = this.#properties();
    const { buildArtwork } = await import(this.setup.assets.artwork);
    // A computer's canvas holds far more than a phone's, so the studio is not held to 16 MP
    const files = await buildArtwork(Object.assign(design, {
      maxPixels: 60000000,
      meta: {
        title: `${this.cfg.name} · ${this.lname()} · ${this.C.name} · ${this.S.size} ${props.Format} ${cm[0] * 10} × ${cm[1] * 10} mm`,
        subject: props._design,
        keywords: [this.cfg.name, this.lname(), this.C.name, this.S.size, props.Format, 'studio print'],
        creator: 'Beyond the Fridge · ' + this.cfg.name + ' · studio print page',
      },
    }));
    // Each picture's dpi on the board: its pixels across the width it is drawn at (cover, zoom),
    // and the dpi the PDF holds it at (no more than 300, nor more than the picture has)
    const pictures = files.measured.filter((it) => it.kind === 'img').map((it) => {
      const s = sizes[it.src] || {};
      const dpi = s.w ? Math.round((s.w * 25.4) / it.w) : 0;
      return {
        what: s.what || 'picture',
        big: !!s.big,
        px: s.w ? `${s.w} × ${s.h}` : '',
        frameCm: [it.clip.w / 10, it.clip.h / 10].map((n) => +n.toFixed(1)),
        dpi,
        pdfDpi: it.dpi || Math.min(300, dpi),
      };
    });
    return { files, pictures, cm, base: `${this.cfg.key}-${this.S.size.toLowerCase()}` };
  }
}

if (!customElements.get('btf-maker')) customElements.define('btf-maker', BtfMaker);
